import { SnapshotTraceBuffer, type SnapshotReason, type SnapshotTrace } from '../lib/telemetry/snapshotTrace';
import { readFeedPairs, type FeedPair } from '../stores/feedPairOwnership';
/**
 * HydraClient — browser WebSocket to Hydra's consumer `/ws` (deployed API is typically port 8891, e.g. `ws://hydra.newbx.com:8891/ws`).
 * Protocol: Hydra repo `CONSUMER_API.md`.
 *
 * **Auth:** Hydra's `ConsumerWebSocketServer` validates `Authorization: Bearer <consumerBearerToken>`
 * when that env is set. The browser `WebSocket` API cannot set custom headers, so this client connects
 * with the URL only. Use an open Hydra (no consumer token), same-origin proxy, or a non-browser client
 * if your deployment requires Bearer on the socket.
 *
 * **Connection sharing:** When the browser supports `SharedWorker`, all g1xl tabs in the
 * same browser session share a single upstream WS via `src/workers/hydraWorker.ts`.
 * This satisfies Hydra's 1-WS-per-service rule (HYDRA_API.md:16-55). Tabs in browsers
 * without SharedWorker support fall back to one direct WS each (legacy behaviour).
 *
 * **Protocol (matches Hydra consumer docs):**
 *   Client → Server: { op: 'subscribe', channel: 'all' | 'markets' | 'orderbooks' }
 *                     { op: 'snapshot', channel: 'markets' | 'orderbooks' }
 *                     { op: 'ping', ts: number }
 *   Server → Client: market | orderbook | pong | backpressure | error
 */

import { captureSourceAge, receiveSourceAge, type SourceAge, type SourceAgeTransfer } from '../lib/sourceAge';
import { noteConnectionEvent } from '../lib/telemetry/connectionEvents';
import { SourceContinuityTransport, parseSourceContinuity, type SourceContinuity, type SourceLane } from '../lib/gameState/sourceContinuity';
import { withSourceEvidence, type SourceObservation } from '../lib/gameState/sourceEvidence';
import { connectMarketOps, marketsSnapshotOp, replaceMarketFilterOp } from '../workers/sportFilter';
import {
  FeedFilterAggregator,
  FilterFence,
  buildMarketFilter,
  buildOpFilter,
  filterKey,
  applyAltOnlyMute,
  readFilterAxes,
  scopeMarketFilter,
  FloorScopeMemory,
  FLOOR_SETTLE_MS,
  FLOOR_SETTLE_MAX_MS,
  type AggregatedNeeds,
  type FeedNeeds,
  type FilterResetMsg,
  type HydraChannelFilterAckMsg,
  type HydraFilterAckMsg,
  type HydraMarketRemovedMsg,
  type MarketFilter,
  type MarketFilterEffective,
  type FencePair,
  type OpFilter,
} from '../workers/feedFilter';
import { ChannelFilterLink, type ChannelLinkMode } from '../workers/channelFilterLink';
import { resolveFeedFilterMode, type FeedFilterMode } from './feedFilterConfig';
import { ORDERBOOK_UNSUBSCRIBE_GRACE_MS } from '../workers/subscriptionAggregator';
import { altOnlyFeedTokens } from '../lib/feedSport';
import { countReceivedBook, countReceivedConsensus, countReceivedMarket, countReceivedOpOdds } from '../lib/telemetry/feedCounters';
import type { KalshiPriceRange } from '../types';

export type { HydraFilterAckMsg, HydraMarketRemovedMsg, MarketFilterEffective, FencePair } from '../workers/feedFilter';

// ── Hydra message types ──────────────────────────────────────────

/** Kalshi series classes on the row (`class`, hydracore manifest-v2 §3.1). */
export type KalshiSeriesClass =
  | 'moneyline'
  | 'spread'
  | 'total'
  | 'btts'
  | 'futures'
  | CfbGameEventClass;

/** The CFB game-event subset g1 renders on GameDetail's Events tab
 *  (hydracore cfb-game-props). See `class` below. */
export type CfbGameEventClass =
  | 'half-fulltime'
  | 'first-td-team'
  | 'first-to-score'
  | 'overtime'
  | 'two-point'
  | 'every-quarter-btts'
  | 'highest-quarter'
  | 'team-stat'
  | 'team-total'
  | 'total-touchdowns'
  | 'total-fieldgoals'
  | 'player-prop'
  // NFL game lines + game props (hydracore nfl-game-lines-props, 2026-09-03;
  // pending core deploy — render when the class appears, rule 8 until then).
  // `race-to`: KXNFLRACE-<event>-<points>-<TEAM|NONE>, a 3-way per points
  // line; `canonicalYesTeam` on the team legs, absent on NONE; the points
  // line is the THIRD ticker segment (no wire field — the one place a
  // segment is parsed). `both-teams-points`: KXNFLBOTH-<event>-<points>,
  // YES when BOTH teams score ≥ `floorStrike` (a WHOLE number, not N − 0.5).
  | 'race-to'
  | 'both-teams-points';

export interface HydraMarket extends SourceObservation {
  ticker: string;
  eventTicker: string;
  /**
   * Kalshi matching-engine shard identity, forwarded by shard-aware producers.
   * Optional while older Hydra versions remain in service. Missing is unknown,
   * never shard zero; routing decisions live outside this read model.
   */
  exchangeIndex?: number;
  // Kalshi parent event series ticker, e.g. KXNBAWEST. Forwarded by newer
  // Hydra builds so consumers can classify sibling families without
  // hardcoding every raw market prefix.
  seriesTicker?: string;
  title: string;
  eventTitle?: string;
  // Per-market subtitle forwarded from Kalshi's `yes_sub_title`. Carries
  // the canonical line on spread + total frames — e.g. "Over 203.5 points
  // scored" or "Montreal wins by over 2.5 goals". Single source of truth
  // for line derivation; no per-sport bucket math needed when present.
  // Nullable on the wire: hydraedge sends an explicit `null` on Polymarket
  // frames (Poly carries no Kalshi-shaped subtitle). Typed to match.
  subtitle?: string | null;
  status: string;
  sport?: string;
  /** ALT pseudo-sports (main-alt-partition-he6-he7): the engine's BASE sport
   *  for a row served under a derived key (`sport:'NFLALT'` → `baseSport:
   *  'NFL'`). Optional on the wire — absent on rows from engines that
   *  predate the split and on every base-key row (`sport === baseSport`).
   *  g1 prefers it and falls back to the token map (`altBaseSport`). */
  baseSport?: string;
  /** g1-STAMPED at the ingest boundary (`stampFeedSport`), never on the
   *  wire: the RAW wire sport key of a row that was folded to its base for
   *  display (`'NFLALT'` on a row whose `sport` now reads `'NFL'`). Absent on
   *  every base-key row. Ownership, filter scope and book routing read
   *  `feedSport ?? sport`; everything the user sees reads `sport`. */
  feedSport?: string;
  /** Scope of play. 'full' (default / full game) or a period: '1H' (1st half,
   *  Kalshi + Poly), '1Q'–'4Q' (single quarters, Kalshi-only), '2H' (soccer
   *  Game Lines), 'F5' (MLB First 5 Innings, Kalshi-only), or '1P'–'3P' (NHL
   *  periods, Kalshi-only). Set by hydra at discovery from the ticker so
   *  consumers bucket period markets WITHOUT re-parsing. See HYDRA_API.md
   *  "Period identity". */
  period?: 'full' | '1H' | '2H' | 'F5' | '1Q' | '2Q' | '3Q' | '4Q' | '1P' | '2P' | '3P';
  category?: string;
  yesBid?: number;  // cents
  yesAsk?: number;  // cents
  noBid?: number;
  noAsk?: number;
  lastPrice?: number;
  volume?: number;
  openInterest?: number;
  // Scheduled start time of the parent event (game/match/fight), ISO UTC.
  // Sourced by Hydra from Polymarket Gamma `gameStartTime` and Kalshi
  // milestone `start_date` when available. With canonicalGameKey collapsing
  // venue siblings onto one row, one source can power the cross-venue display.
  // Undefined when upstream metadata has no start signal.
  eventStartTime?: string;
  /** Venue discriminator. `polymarket_us` (V55) is the CFTC venue; frames
   *  carry the additive `pmus*` keys below and are keyed by `PMUS-` tickers.
   *  marketStore drops any value outside this union at ingest (F0). */
  source: 'kalshi' | 'polymarket' | 'polymarket_us';
  // Polymarket US-only (V55). Orders address `pmusMarketSlug`; the tick and
  // minimum quantity are per market (0.5¢ ticks and fractional contracts
  // exist); `feeCoefficient` is Θ in fee = Θ·C·p·(1−p).
  pmusMarketId?: string;
  pmusMarketSlug?: string;
  /** The venue's own market vocabulary: `moneyline` / `spreads` / `totals`, and
   *  the finer `sportsMarketType` (`football_team_first_half_spread`). Hydra's
   *  plane `marketType` is `'game'` for EVERY US game line, so these two are
   *  the only way to tell a first-half spread from the full-game moneyline. */
  pmusMarketType?: string;
  sportsMarketType?: string;
  orderPriceMinTickSize?: number | string;
  minimumTradeQty?: number | string;
  feeCoefficient?: number | string;
  lastUpdateMs: number;
  /**
   * Kalshi-only authoritative banded order-price grid. Optional while mixed
   * Hydra versions are in service.
   */
  priceRanges?: KalshiPriceRange[];
  // Hydra-normalized identifiers. Present on dan-dev+; undefined on older
  // Hydra builds — consumers must tolerate that. See hydra/HYDRA_API.md.
  canonicalGameKey?: string;   // "sport|YYYY-MM-DD|TEAM_A|TEAM_B", alpha-sorted
  // Doubleheader-safe key (additive, dual-key — emitted alongside the
  // unchanged canonicalGameKey). Shape: "sport|YYYY-MM-DD|gameNo|TEAM_LO|TEAM_HI"
  // — lowercase sport, alpha-sorted teams, integer gameNo (1 for a normal
  // single game, >1 only for a doubleheader's later games). Uniform 5-seg for
  // every game, so consumers can group/address by it unconditionally. Two games
  // of the same matchup on one date share canonicalGameKey but differ here, so
  // grouping on this field keeps their odds + score un-blended. Absent on a
  // market discovered before Unabated's bootstrap populates (self-corrects on the
  // next discovery refresh) — fall back to canonicalGameKey then, UNLESS the
  // frame is ambiguous (see `dhAmbiguous` below, and the belt-and-braces
  // sibling check in marketStore for pre-`dhAmbiguous` producers), in which
  // case there is no safe fallback and the frame must fail closed.
  // See HYDRA_API.md.
  canonicalGameKeyV2?: string;
  // Fail-closed doubleheader signal (DNL-001). Set by hydraedge ONLY when it
  // proved the base key is a doubleheader day but could NOT say which game
  // this frame belongs to; `canonicalGameKeyV2` is then absent by producer
  // invariant (hydraedge ConsumerHub emits the flag only when true, so every
  // other frame is byte-identical to before). Consumers must NOT promote the
  // legacy key to gameNo=1 for such a frame — that would attach it to GAME 1
  // and put a wrong-game start time (and wrong-game trade context) on a
  // tradeable card. marketStore suppresses these from rows / ticker selection
  // / start-time slots while keeping them observable diagnostically; see
  // `resolveGameKeyV2` there and HYDRA_API.md "dhAmbiguous".
  dhAmbiguous?: boolean;
  canonicalAwayCode?: string;  // Kalshi only (Poly slugs order by YES-outcome)
  canonicalHomeCode?: string;  // Kalshi only
  // Source-agnostic alpha-sorted canonical pair. Set on both Kalshi and
  // Polymarket frames whenever the market parses into a game (per-day
  // moneyline / spread / total). Lets the row collapse off raw Poly slug
  // aliases (NYL→NY, POR→PDX, LAS→LV, GSV→GS for WNBA) even when Kalshi
  // hasn't listed the game yet. Not game-day-positional — use
  // canonicalAwayCode/canonicalHomeCode for that.
  canonicalTeams?: [string, string];
  /** Numeric line for a spread/total market — the STRUCTURED replacement for
   *  parsing Kalshi's `subtitle` prose. Added by hydraedge 2026-07-31
   *  (main @ e8fefad) on both `/ws` and REST; additive, `subtitle` still
   *  carries the prose. Present on both venues, expressing the same fixture's
   *  line as the same number.
   *
   *  ABSENT IS NOT ZERO. A moneyline legitimately has no line, and defaulting
   *  to 0 renders a bogus 0.5 line — treat absent as "not a line market" and
   *  fall through to the prose/bucket parse (OG hydra frames do not carry it).
   *  Necessary because Kalshi's spread wording is not stable across series:
   *  the World Cup says "Goal Diff Reg Time: England wins by more than 5.5
   *  goals" while MLS/UEL/UCL say "New York City wins by more than 2.5 goals". */
  floorStrike?: number | null;
  /** Upper bound for a RANGE market (both ends). Currently always absent —
   *  hydra ships no range markets yet. Reserved so consumers can distinguish a
   *  range from the one-sided `floorStrike` when they appear. */
  capStrike?: number | null;
  // Polymarket UFC frames only (2026-07-30). Order-independent COMPLETE-name
  // identity of the fight: both outcomes as playerKey-style slugs, alpha-sorted
  // and pipe-joined ("anderson-silva|joao-santos"). Exists because even
  // full-surname canonical keys are lossy — two same-day fights can share a
  // surname pair — and it is the evidence hydra's ambiguity guard uses.
  // INFORMATIONAL for consumers (disambiguation / display); the pairing
  // contract remains canonicalGameKey, which hydra WITHDRAWS entirely when a
  // key is ambiguous. See HYDRA_API.md "UFC fight markets".
  participantKey?: string;
  // Soccer 3-way / future N-outcome markets: canonical outcome that this
  // binary market's YES side pays on. Team code or "DRAW". Kalshi's raw draw
  // suffix is TIE and Polymarket's is draw; Hydra normalizes both here.
  // Nullable on the wire: spreads and totals carry an explicit `null` BY
  // DESIGN (OG parity - the edge leaves them unstamped). Never filter a
  // spread/total on this being present.
  yesOutcome?: string | null;
  /** What a 1X2 leg's YES pays on, by kind: `team` (with `canonicalYesTeam`),
   *  `draw`, or `btts`. Core stamps it on every soccer GAME leg (hc1 sends no
   *  `yesOutcome`), so a draw leg is identifiable without a ticker parse.
   *  Absent on older engines. */
  outcome?: 'team' | 'draw' | 'btts' | (string & {}) | null;
  // Market-type discriminator forwarded by hydra on soccer / derivative frames.
  // 'advance' = the knockout "To Advance" 2-way (who advances incl. ET + pens —
  // a DISTINCT market + settlement from the regulation 1X2, even though it shares
  // the 1X2's canonicalGameKey). marketStore cross-checks this against the
  // locally-parsed marketType so advance markets bucket APART from the 3-way and
  // never pollute it. Absent on older builds / non-soccer frames — consumers must
  // tolerate that and fall back to the parsed type. See HYDRA_API.md "Soccer To
  // Advance".
  // NOTE `'game'` is what hydraedge actually sends on soccer club-league
  // frames — for moneylines AND spreads/totals alike, so it does NOT
  // discriminate bet type. Use the series (KX…SPREAD / KX…TOTAL) or
  // `floorStrike`'s presence for that; this field is only load-bearing for the
  // 'advance' cross-check.
  // `'future'` = a DATELESS, gameless outright (tournament winner, season win
  // total, award). hydraedge stamps it on the Polymarket season-futures branch
  // (upstream/polymarket/discovery.ts) and it is the ONLY reliable signal that
  // a frame is futures-shaped — those tickers carry no date, no teams, and no
  // canonical keys for a consumer to infer one from. `'prop'` rides the same
  // branch for dated per-game props. Both are additive: g1's game selectors
  // ignore them, and getFutureBoards keys off `'future'` exclusively.
  //
  // WARNING for the futures path: `eventTicker` on a future frame is the
  // market's own conditionId, NOT a container — the Gamma event slug that
  // names the tournament is dropped at the edge. Group by the slug
  // (lib/futures/parseFutureSlug), never by eventTicker.
  marketType?: 'moneyline' | 'spread' | 'total' | 'series' | 'btts' | 'advance' | 'game' | 'future' | 'prop';
  /** The Kalshi SERIES CLASS on the row (hydracore cfb-game-props, main @
   *  eb77800, 2026-08-30 — HYDRA_API.md "Kalshi identity records"): core
   *  stamps it on every Kalshi identity record and edges forward it. This is
   *  what a consumer renders BY — class + `period` + `subtitle` (+
   *  `canonicalYesTeam` for sided rows) — never by re-parsing the ticker
   *  (CFB's 5-letter codes and `GTGT`/`TIEGT` combo suffixes defeat a
   *  client parser). `marketType` stays the coarse `game | future`
   *  discriminator it always was. Values: moneyline, spread, total, btts,
   *  team-total, half-fulltime, overtime, two-point, first-to-score,
   *  first-td-team, highest-quarter, every-quarter-btts, total-touchdowns,
   *  total-fieldgoals, team-stat (+ `stat`), player-prop, futures. Unknown
   *  → ignore the row for pane purposes (rule 8). Typed as the known union
   *  PLUS `string` so a value core ships before this file learns it still
   *  typechecks as "unknown". Absent on frames from an engine that has not
   *  forwarded it yet, and on Polymarket rows. */
  class?: KalshiSeriesClass | (string & {});
  /** `team-stat` rows: which per-team stat the O/U line counts —
   *  `yds | td | fg | int | sack | to | rec | rec-td | rec-yds | rush-att |
   *  rush-td | rush-yds`. `player-prop` rows (hydracore nfl-player-props,
   *  2026-09-03): the player stat family — NFL `pass-yds | pass-td | … |
   *  first-td | anytime-td | multi-td | dst-td | td`, MLB `hits | hr | hrr |
   *  ks | outs | rbi | sb | tb` (g1's own `PropStat` ids, verbatim). Absent
   *  on every other class. */
  stat?: string;
  /** `player-prop` rows only (nfl-player-props): the player's display name
   *  from the row's own subtitle ("Brock Purdy"; absent on the NONE leg) and
   *  the CANONICAL code of the player's team ("SF"; needs the row's oriented
   *  pair — absent when the canonical key is withheld). Core stamps both;
   *  the hydraedge forward is a separate deploy (null on the wire until
   *  then) — consumers prefer them when present and fall back to
   *  `subtitle` + the ticker's player segment. Both replaced whole on every
   *  catalogue re-join; a key revocation clears `playerTeam` and keeps
   *  `player`; a withdrawn row loses both. Never a team side: `canonicalYesTeam`
   *  is withheld on every prop row by class. */
  player?: string | null;
  playerTeam?: string | null;
  /** UFC `fight-prop` rows only (hydracore kalshi-ufc-fight-props, #848):
   *  the bout's prop terms, minted with the inherited key and withheld with
   *  it. `fighter` = the key-pair surname code ("Duncan") and `fighterName`
   *  its display name on fighter legs (method / round of victory); `method`
   *  = ko-tko-dq | submission | decision | draw-nc | decision-draw-nc;
   *  `round` = 1..5 on round-of-victory fighter legs; `line` = N on total
   *  rounds ("ends before round N"). The UFC edge may not forward these yet,
   *  so g1 derives the same terms from the exact ticker suffix and requires
   *  any field that IS present to agree (lib/ufc/fightProps). */
  fighter?: string | null;
  fighterName?: string | null;
  method?: string | null;
  round?: number | null;
  line?: number | null;
  /** op-player-props (hydracore, 2026-09-08): the ladder IDENTITY core mints
   *  once per Kalshi `player-prop` row — canonical `TEAM+PLAYERCODE+jersey`
   *  (`TORGSPRINGER4`, `SFBPURDY13`; alias-safe: ATH→OAK, JAC→JAX). The same
   *  string rides the `oddspapi` channel's `player-prop` entries as
   *  `playerKey` when core paired them, so g1 joins by string equality.
   *  Absent (withheld) when core could not orient the row. g1 keys its
   *  ladders on this when present, the ticker-derived key otherwise (spec
   *  DNL-002 atomic migration: ladder key and OP bucket key move together). */
  playerKey?: string | null;
  // Set on every market frame for series-related tickers (Kalshi
  // KX*SERIES*/KX*SERIESSCORE*/KX*SERIESROADWIN* and Polymarket
  // *-playoffs-who-will-win-series-* slugs). Shape:
  //   "{sport}|series|{YY}|TEAM_A-TEAM_B"
  // Teams alpha-sorted, normalized canonical 3-char codes. Used by
  // marketStore to collapse cross-venue series tickers onto one row,
  // mirroring the canonicalGameKey path for per-day games.
  canonicalSeriesKey?: string;
  // Set on series tickers (Kalshi + Polymarket): the canonical 3-char
  // team code whose win resolves the binary contract YES. On Polymarket
  // slugs the YES team is encoded by slug position (not alpha order),
  // so this is the only reliable per-team mapping for series rows.
  // Falls back to "row's awayCode is YES" when undefined.
  canonicalYesTeam?: string;
  /** canonical-team-names (hydracore, 2026-10-20; HYDRA_API.md "Team names —
   *  `canonicalTeamNames`, all sports"): the GAME's display names, keyed by
   *  the codes of this row's `canonicalGameKeyV2` pair
   *  (`{"NYK":"New York Knicks","PHI":"Philadelphia 76ers"}`). On EVERY row
   *  that carries `canonicalGameKeyV2` — Kalshi, Polymarket, Poly US, every
   *  class, props and ALT rows included — and identical on every keyed row of
   *  a game. One entry when hydra could name one side; absent when neither.
   *  Absent means render the code: never a subtitle, ticker or local table.
   *  g1 reads it through `stores/gameTeamNames` only. */
  canonicalTeamNames?: Readonly<Record<string, string>> | null;
  // Set on every series-related ticker frame (KX*SERIES, KX*SERIESSCORE,
  // KX*SERIESROADWIN, Polymarket who-will-win-series slugs).
  //
  //   seriesWins         — per-team wins keyed by canonical 3-char team code,
  //                        e.g. {BOS: 1, PHI: 2}. Undefined on pre-deploy /
  //                        pre-tracker-warmup frames or Polymarket-only series
  //                        where there are no Kalshi GAME tickers to count.
  //   seriesWinsToClinch — wins required to take the series. 4 for best-of-7,
  //                        3 for best-of-5, 2 for best-of-3. Derived from
  //                        Kalshi's KX*SERIESSCORE outcome shape; falls back
  //                        to 4 (NBA/NHL playoffs are always best-of-7).
  //
  // Used by clinch-injection: skip unless some team is on match point
  // (wins ≥ seriesWinsToClinch − 1). Series-not-started and no-team-on-hill
  // both fall through to "leave Kalshi cells blank when per-day ticker is
  // missing" rather than mis-injecting the series-winner ticker.
  seriesWins?: Record<string, number>;
  seriesWinsToClinch?: number;
  // Polymarket-only token plumbing. Populated by hydra ≥ 2026-05-16 from
  // its tokenRegistry (Gamma `conditionId` + `clobTokenIds[0]=yes,
  // clobTokenIds[1]=no`). Required by g1's trade ticket (GameDetailPanel)
  // to construct CLOB orders without a second lookup at trade time. Older hydra builds
  // leave all three undefined; consumers must tolerate that.
  conditionId?: string;
  yesTokenId?: string;
  noTokenId?: string;
  /**
   * Polymarket-only. True when the market belongs to a NEG_RISK
   * multi-outcome event (mutually-exclusive bundled yes/no markets —
   * NBA Draft first pick, election primary slots, etc.). Forwarded to
   * placeOrder via the v2 SDK or signing fails with `invalid signature`.
   * Older hydra builds leave this undefined; consumers must tolerate
   * that (treat absent as false).
   */
  negRisk?: boolean;
  /**
   * Polymarket-only. CLOB minimum price increment as a stringified
   * decimal — "0.01" for most sports moneylines, "0.001"/"0.0001" for
   * fine-tick binaries. Sourced from Gamma `orderPriceMinTickSize` via
   * hydra's CachedMarket. Forwarded to placeOrder so the v2 CLOB
   * doesn't reject with `invalid tick size (X), minimum for the market
   * is Y` on markets with sub-cent increments. Older hydra builds
   * (pre-2026-05-23) leave this undefined; relay-side `placeOrder`
   * defaults to '0.01' in that case, which is correct for every sports
   * moneyline we trade but wrong for fine-tick binaries — pass the
   * value through whenever it's present.
   */
  tickSize?: string;
}

export interface HydraPriceLevel {
  price: number;  // cents 1-99
  size: number;
}

export interface HydraOrderbook {
  ticker: string;
  yes: HydraPriceLevel[];
  no: HydraPriceLevel[];
  timestamp: number;
}

export interface HydraMarketMsg {
  type: 'market';
  market: HydraMarket;
  serverTs: number;
  seq: number;
  /** A2′ fencing pair — present on EVERY markets frame of a connection that
   *  sent the versioned `filter` (edge + core), absent on legacy frames.
   *  Admission is exact-pair equality against the last ack'd pair
   *  (`FilterFence`); any other stamp — an older generation, a previous
   *  socket — is dropped. */
  socketIncarnation?: string;
  filterGeneration?: number;
}

export interface HydraOrderbookMsg extends SourceObservation {
  type: 'orderbook';
  ticker: string;
  orderbook: HydraOrderbook;
  staleness?: { lastUpdateMs: number; ageMs: number; state: string };
  serverTs: number;
  seq: number;
}

/** Unabated consensus update for one (canonicalGameKey, periodTypeId) pair.
 *  Shape matches `UnabatedGameState.consensus` on /api/analytics/game-states
 *  REST so consumers don't need a second mental model. Emitted on ≥1¢ shift
 *  on either prob; backfilled on subscribe for periodTypeId=1 only. */
export interface HydraConsensus {
  canonicalGameKey: string;
  // Doubleheader-safe key carried alongside the unchanged canonicalGameKey —
  // "sport|YYYY-MM-DD|gameNo|TEAM_LO|TEAM_HI". For a doubleheader both games'
  // consensus frames arrive under the same canonicalGameKey but differ here, so
  // marketStore caches consensus by this key to keep the two games un-blended.
  // Absent only pre-bootstrap (fall back to canonicalGameKey). See HYDRA_API.md.
  canonicalGameKeyV2?: string;
  awayTeam: string;     // Unabated's own away/home — swap when landed in canonicalGameKey's homeCode slot
  homeTeam: string;
  periodTypeId: number; // 1 = full game
  periodLabel: string;
  /** Market discriminator. Absent = the regulation result (1X2 / moneyline) —
   *  the unchanged contract. 'advance' = the soccer knockout "To Advance" 2-way
   *  (who progresses incl. ET + pens), broadcast ADDITIVELY on the SAME
   *  canonicalGameKey + periodTypeId as the result frame, so consumers MUST
   *  bucket by (gameKey, periodTypeId, marketType) or the two clobber each
   *  other. 2-way: away/home only, no draw. See HYDRA_API.md "Soccer To Advance". */
  marketType?: 'advance';
  awayFairProbCents: number;
  homeFairProbCents: number;
  /** Soccer 3-way (Unabated betTypeId 98): the Draw side's fair probability.
   *  Absent/null on binary sports AND on the 'advance' 2-way (it has no draw —
   *  absent, not 0/null). Sides don't sum to 100 minus vig the way a binary pair
   *  does — render each independently. */
  drawFairProbCents?: number | null;
  /** Sub-cent trend feed (since 2026-07-05): fair probabilities at
   *  tenth-of-a-cent resolution — integer tenths 0–1000, sides summing to
   *  exactly 1000, draw soccer-only like the cents. ABSOLUTE values, not
   *  deltas (backpressure coalesces latest-wins) — diff consecutive frames
   *  client-side (lib/consensusTrend). Display surfaces (dashboard Odds
   *  column, panel U-row, PositionTracker) render tenths as the odds when
   *  present, falling back to the integer cents fields above. Optional only
   *  for pre-rollout hydra. */
  awayFairProbTenths?: number;
  homeFairProbTenths?: number;
  drawFairProbTenths?: number | null;
  /** Sharp-anchor flag (since 2026-07-05): true ⇒ Unabated marketSourceId 7
   *  (Pinnacle's sharp feed) anchored this consensus at 4× a field book's
   *  weight; false ⇒ field-only (src 7 had no viable quote or was struck).
   *  A flip alone triggers a frame even with zero numeric change. false is
   *  the NORMAL sparse-source state, not an error. Absent = pre-rollout. */
  sharpBookUsed?: boolean;
  contributingBooks: number;
  lastUpdateMs: number;
}

export interface HydraConsensusMsg {
  type: 'consensus';
  consensus: HydraConsensus;
  serverTs: number;
  seq: number;
}

// ── OddsPapi comparison odds (channel `oddspapi`) ────────────────
// Mirrors hydra's frozen `ServerOpOddsMessage` / `ServerOpSnapshotBegin
// Message` / `ServerOpSnapshotEndMessage` (HYDRA_API.md §"OddsPapi odds").
// Read-only comparison feed beside Unabated — never consensus, never a
// failover, never an execution input.

/** One bookmaker's quote. Decimal odds are STRINGS (hydra's canonical
 *  number→string serialization — contract rule 6); convert for display
 *  only. `*American` fields are the PROVIDER's own conversion — consumers
 *  prefer them and locally convert from the decimal string only when
 *  absent (rule 6b). `drawDecimal` present ⇔ 3-way market (rule 5). */
export interface HydraOpBookQuote {
  bookmaker: string;
  awayDecimal: string;
  homeDecimal: string;
  drawDecimal?: string;
  awayAmerican?: number;
  homeAmerican?: number;
  drawAmerican?: number;
  /** Hydra-computed single-book de-vig (tenths 0–1000, sides sum 1000) —
   *  the de-vigged Pinnacle display line (rule 6c). HYDRA computes fair
   *  values, never this client. Present only on the displayed anchor
   *  book; absence is a renderable state (no secondary line). */
  awayFairTenths?: number;
  homeFairTenths?: number;
  drawFairTenths?: number;
  /** Provider update time (epoch ms) — the per-book freshness source.
   *  Never use the frame's `serverTs` for freshness (rule 7); this
   *  timestamp also governs the de-vigged-Pinnacle secondary AND the
   *  anchor-only state (rule 7 as amended — per-displayed-value). */
  /** Vendor per-side limit. Sportsbook max stake in USD; exchange rows
   * carry the vendor venue limit. Absent is unknown, never an implied zero. */
  awayLimitMax?: number;
  homeLimitMax?: number;
  drawLimitMax?: number;
  updatedAtMs: number;
}

/** Cross-book de-vigged fair value, hydra-computed by the SAME algorithm
 *  as Unabated's consensus (extracted, not imitated — rule 6c). Absent
 *  ⇔ no computable value exists (mirrors Unabated's low-book behavior);
 *  consumers then render anchor-only, or `—` when the anchor fair value
 *  is also absent. Never computed or adjusted client-side. */
export interface HydraOpConsensus {
  awayFairProbCents: number;
  homeFairProbCents: number;
  /** Soccer 3-way only. */
  drawFairProbCents?: number;
  /** Integer tenths (0–1000), sides sum 1000 — the display precision. */
  awayFairProbTenths: number;
  homeFairProbTenths: number;
  drawFairProbTenths?: number;
  contributingBooks: number;
  /** Analogue of Unabated's sharpBookUsed — pinnacle anchored at elevated
   *  weight; false = field-only (normal sparse state, not an error). */
  pinnacleAnchored: boolean;
  /** THE freshness authority for the consensus primary (rule 7 as
   *  amended); `OpGameOdds.lastUpdateMs` is barred for displayed values. */
  lastUpdateMs: number;
}

/** A previously-quoting book whose price is currently withheld, WITH the
 *  provider's explicit reason (phase 3, rule 12). Producer-side the entry
 *  is never inferred from absence, attribution is contemporaneity-fenced,
 *  and the withhold is debounced (30s sustain) — consumers render it
 *  verbatim and MUST NOT rebuild inference on top of it. `reason` is an
 *  open union ('suspended' | 'stale_link' | 'no_odds' |
 *  'market_inactive' today); unknown values render as generic
 *  "withdrawn" (additive tolerance). `sinceMs` = start of the CONTINUOUS
 *  withhold — the ONLY age authority for the displayed state (rule-7
 *  discipline; serverTs and op.lastUpdateMs barred). */
export interface HydraOpWithdrawnBook {
  bookmaker: string;
  reason: string;
  sinceMs: number;
}

/** Open union — unknown marketType values must be ignored (rule 8). */
export interface HydraOpMarket {
  marketType: string;
  /** op-player-props R1 (hydracore HYDRA_API §"Player props on `oddspapi`",
   *  2026-09-08) — present IFF `marketType === 'player-prop'`: core's stat
   *  vocabulary (`hits`, `pass-yds`, `td`, …), the vendor's playerId, the
   *  display name when the roster resolved it, and `playerKey` (Kalshi
   *  canonical TEAM+CODE+jersey) IFF the name joined a Kalshi `player-prop`
   *  row of the same game. `pairing` is the explicit wire state, never
   *  absent on a prop entry: `paired` ⇒ `playerKey` present and `points`
   *  equals that Kalshi row's floorStrike; `unpaired` ⇒ game + stat +
   *  player only, NO `playerKey` (the bots' narrowing feed — g1 ignores
   *  these in v1). OVER rides `awayDecimal`, UNDER `homeDecimal`. */
  stat?: string;
  playerId?: number;
  player?: string;
  playerKey?: string;
  pairing?: 'paired' | 'unpaired';
  /** Present IFF the market is FULL-GAME (value 1) — full-feed B
   *  (hydracore §"Full-feed B", 2026-08-17): period-scoped entries carry
   *  NO periodTypeId; they carry `period` instead. NOTE the addendum's
   *  precision: `periodTypeId === 1` selects ALL full-game markets
   *  including the new families (team totals, odd/even) — what keeps a
   *  pre-B view rendering its old set is the ignore-unknown-marketType
   *  discipline, not this field. */
  periodTypeId?: number;
  /** Vendor period string, VERBATIM, present IFF the market is NOT
   *  full-game: 'p1+p2+p3+p4+p5' = F5, 'p1+p2' = 1H, 'p3+p4' = 2H,
   *  'pN' = single period/quarter/inning, plus OT combos. The JOIN key
   *  for period-scoped lookups — periodLabel is a display hint only. */
  period?: string;
  periodLabel: string;
  /** THE LINE (hydracore §"Spread & total markets", 2026-08-11; widened
   *  by §"Full-feed B", 2026-08-17). Present IFF the family carries a
   *  line: 'spread' | 'total' | 'teamtotal-home' | 'teamtotal-away' |
   *  'total-touchdowns' | 'total-fieldgoals' — and absent on period
   *  moneylines and odd/even. Spread: the HOME team's handicap
   *  exactly as the provider declares it (home −1.5 ⇒ -1.5; away's implied
   *  handicap is the negation). Total: the over/under line. One line = one
   *  market entry; books never mix across lines and the consensus is
   *  computed among THAT line's books only. Side naming rides the existing
   *  quote fields (sideIndex convention): spread away/home slots are the
   *  away/home sides at that line; total awayDecimal is OVER, homeDecimal
   *  is UNDER. */
  points?: number;
  books: HydraOpBookQuote[];
  consensus?: HydraOpConsensus;
  /** Rule 12 — absolute per-frame state like everything else: presence
   *  lists current withholds, absence means none. No client latching. */
  withdrawnBooks?: HydraOpWithdrawnBook[];
}

export interface HydraOpOdds {
  /** Which comparison feed produced this frame — only OddsPapi is served
   *  (the OpticOdds trial channel was removed 2026-09-06 with the vendor). */
  provider: 'oddspapi';
  /** Provider event id, diagnostics only — consumers route on the
   *  canonical keys. As-built wire carries a NUMBER; workspace §5 says
   *  string (G1-DNL-002) — typed tolerant, never consumed. */
  fixtureId: number | string;
  canonicalGameKey: string;
  canonicalGameKeyV2: string;
  sport: string;
  /** Provider's own orientation mapped to canonical codes — align to the
   *  row exactly as for consensus (`awayTeam === row.homeCode` ⇒ swapped,
   *  rule 4). */
  awayTeam: string;
  homeTeam: string;
  lastUpdateMs: number;
  /** ABSOLUTE per-game state; `[]` is an explicit no-coverage TOMBSTONE —
   *  delete the entry (rule 1). */
  markets: HydraOpMarket[];
}

export interface HydraOpOddsMsg {
  type: 'opOdds';
  /** Legacy 4-seg canonicalGameKey (top-level: per-game coalesce key +
   *  tickers[] filter). Bucket by `op.canonicalGameKeyV2` (rule 3). */
  ticker: string;
  sport: string;
  /** Present ⇔ the frame belongs to a snapshot burst. */
  generation?: number;
  /** Opaque producer-incarnation id — HYDRA's process, not the provider's
   *  gateway `serverEpoch` (unrelated; never on this wire). Equality-
   *  compared only (rule 2e). */
  sourceEpoch: string;
  /** Per-game source revision, monotonic within one `sourceEpoch` — the
   *  causal-ordering authority; arrival order is never trusted (rule 2b). */
  rev: number;
  op: HydraOpOdds;
  serverTs: number;
  seq: number;
}

export interface HydraOpSnapshotBeginMsg {
  type: 'opSnapshotBegin';
  generation: number;
  sourceEpoch: string;
  serverTs: number;
  seq: number;
}

export interface HydraOpSnapshotEndMsg {
  type: 'opSnapshotEnd';
  generation: number;
  sourceEpoch: string;
  /** Frames sent in the burst — the consumer's integrity check (rule 2). */
  gameCount: number;
  serverTs: number;
  seq: number;
}

// ── Game state (channel `gamestate`) ─────────────────────────────
// Absolute per-game state, keyed by the v2 canonical game key, with the same
// Begin/End snapshot-burst protocol as the odds channels. This replaces the
// 5 s poll of /api/analytics/game-states for everything that MOVES: hydra
// emits on every served change, so the wire is both fresher and cheaper than
// polling ever was (measured 2026-09-10: ~27 gameState frames/s on the live
// fleet against one batched fetch every 5 s).
//
// One field the REST response has and this frame does not: `consensus`. That
// is assembled at read time from the core's OWN oddspapi cache and rides no WS
// channel (a `consensus` frame type exists in this client but hydracore emits
// none — verified live). So the poll survives, slowed right down, for that one
// block; see `useGameStatesPoll`.

/** `state` is null on a RETIRED tombstone — the lane no longer asserts this
 *  game and a consumer must drop it rather than keep the last score. */
export interface HydraGameStateMsg {
  type: 'gameState';
  /** v2 canonical game key (`{sport}|{date}|{gameNo}|{lo}|{hi}`). */
  ticker: string;
  sport: string | null;
  /** Present IFF this frame belongs to a snapshot burst. */
  generation?: number;
  sourceEpoch: string;
  /** Monotonic within `sourceEpoch` — the sole causal authority. Compare
   *  before applying so a delayed frame cannot overwrite a newer one. */
  rev: number;
  state: Record<string, unknown> | null;
  serverTs: number;
  seq: number;
}

export interface HydraGsSnapshotBeginMsg {
  type: 'gsSnapshotBegin';
  generation: number;
  sourceEpoch: string;
  serverTs: number;
  seq: number;
}

export interface HydraGsSnapshotEndMsg {
  type: 'gsSnapshotEnd';
  generation: number;
  sourceEpoch: string;
  gameCount: number;
  serverTs: number;
  seq: number;
}

/** Active-sport set meta frame (channel `markets`, HYDRA_API.md §Active
 *  sports). Sorted uppercase hydra wire codes for every sport with ≥1
 *  currently-live market. Sent on every markets/all subscribe (before any
 *  market frame) and re-broadcast on set change. Each frame is the complete
 *  replacement set — never a delta — and is NOT scoped by the consumer's
 *  sports[] filter (discovering sports outside the filter is the point). */
export interface HydraActiveSportsMsg {
  type: 'activeSports';
  sports: string[];
  activeFeeds?: Array<{ sport: string; venue: string }>;
  serverTs: number;
  seq?: number;
  /** PROPOSED (g1xl-v2#1053 Part 3): filter axes this engine understands
   *  beyond the shipped contract (`'lineScope'`). Absent on every engine
   *  today; the consumer never sends an axis the announce did not list. */
  filterAxes?: string[];
}

type HydraServerMsg =
  | SourceContinuity
  | { type: '__sourceContinuityInvalid'; lane?: SourceLane }
  | HydraMarketMsg
  | HydraOrderbookMsg
  | HydraConsensusMsg
  | HydraActiveSportsMsg
  | HydraFilterAckMsg
  | HydraMarketRemovedMsg
  | { type: 'orderbookUnavailable'; ticker: string; sport: string | null; reason: string; serverTs: number; seq: number }
  | HydraOpOddsMsg
  | HydraOpSnapshotBeginMsg
  | HydraOpSnapshotEndMsg
  | HydraGameStateMsg
  | HydraGsSnapshotBeginMsg
  | HydraGsSnapshotEndMsg
  | { type: 'pong'; clientTs: number; serverTs: number }
  | { type: 'backpressure'; level: number; pending: number; message: string }
  | { type: 'error'; message: string };

// Internal control messages from the SharedWorker. `__greet_begin`/`__greet_end`
// bracket the worker's cache replay to a newly-attached port so the tab can
// distinguish replayed frames from live upstream frames (see
// `liveMarketFramesReceived`). `__alive` is the worker's liveness tick for a
// quiet filtered upstream (pong-backed) — see hydraWorker upstreamAliveMs().
// `__stats` is main's per-connection transport tally (#1053 Part 0).
// `__snapshotTrace` replays/streams the worker's snapshot-request trace.
type SnapshotTraceMsg = { type: '__snapshotTrace'; entries?: SnapshotTrace[]; entry?: SnapshotTrace };
type WorkerControlMsg =
  | SnapshotTraceMsg
  | { type: '__status'; connected: boolean }
  | { type: '__greet_begin' }
  | { type: '__op_alive' }
  | { type: '__greet_end' }
  | { type: '__alive'; at: number }
  | FilterResetMsg
  | WorkerStatsMsg
  | WorkerOpLinkMsg;
/** The worker's `oddspapi` filter state on its upstream socket
 *  (odds-gamestate-feed-filters): broadcast on every change and in greet. */
type WorkerOpLinkMsg = { type: '__opLink'; mode: ChannelLinkMode; installed: boolean; filterGeneration: number | null };

/** Per-connection transport tally (g1xl-v2#1053 Part 0): what this
 *  browser pulls from ONE engine over the last `intervalMs`. Shared mode:
 *  the worker's 5 s tick (`__stats`, one upstream socket shared by `ports`
 *  tabs). Direct mode: counted on this window's own socket. */
export interface TransportStats {
  at: number;
  intervalMs: number;
  frames: number;
  /** UTF-16 code units of the raw text frames ≈ bytes for hydra's JSON. */
  bytes: number;
  byType: Record<string, number>;
  /** Tabs sharing the worker (shared) or 1 (direct). */
  ports: number;
  filterGeneration: number | null;
  legacyFallback: boolean;
  /** Installed worker scope, for late-tab diagnostics only. Older workers
   *  omit it; this must never be used to request or authorize market data. */
  filterScope?: import('../lib/feedFilter/transportScope').TransportFilterScope | null;
  /** filterAcks dropped for carrying no channel this transport speaks
   *  (directory-edgelike-cores D5) — cumulative per worker / client. Older
   *  workers omit it. Diagnostics only. */
  untaggedFilterAcks?: number;
  /** The `oddspapi` filter on this connection (odds-gamestate-feed-filters):
   *  its mode on the socket, installed generation and drop counters. Null /
   *  absent with the odds filter off. Diagnostics only. */
  opFilter?: {
    mode: ChannelLinkMode;
    filterGeneration: number | null;
    stampedOnLegacy: number;
    unadmitted: number;
    expiries: number;
    rejected: number;
  } | null;
}
type WorkerStatsMsg = TransportStats & { type: '__stats'; connected: boolean };

// ── Callbacks ────────────────────────────────────────────────────

/** hydra's per-book staleness block (present on orderbook frames from a
 *  Stage-B engine; absent on legacy frames). */
export type HydraOrderbookStaleness = NonNullable<HydraOrderbookMsg['staleness']>;

export interface HydraCallbacks {
  /** Ordered, connection-scoped evidence. Null with a lane quarantines that lane;
   * null alone revokes the connection. Not a heartbeat or freshness verdict. */
  onSourceContinuity?: (control: SourceContinuity | null, invalidLane?: SourceLane) => void;
  /** `age` is main's source-qualified age evidence. Its `publicationMs` is the
   *  frame's broadcast stamp and its `ageMs` hydra's own age read — the two
   *  values streamrift's feed→screen probe (`feedLatencyProbe`) used to take as
   *  separate `serverTs` / `staleness` arguments. One contract, both uses. */
  onMarket: (market: HydraMarket, age?: SourceAge) => void;
  onOrderbook: (ticker: string, book: HydraOrderbook, age?: SourceAge, observation?: SourceObservation) => void;
  /** `serverTs` is the broadcast time of the frame — on a live update it
   *  equals `consensus.lastUpdateMs`; on a snapshot-burst re-delivery it can
   *  be minutes later. The gap dates trend/recency state (a stale snapshot
   *  must seed dim, not flash). */
  onConsensus: (consensus: HydraConsensus, serverTs?: number, age?: SourceAge) => void;
  /** Complete replacement set of sports with ≥1 live market (uppercase wire
   *  codes; may include codes g1 doesn't render — consumer filters). Fires
   *  on every (re)subscribe and on set change. */
  onActiveSports?: (sports: string[], filterAxes?: string[], activeFeeds?: FeedPair[]) => void;
  onStatus: (connected: boolean) => void;
  /** Fires on EVERY server frame, pong and control frames included —
   *  "the transport is alive", regardless of whether any market data
   *  changed. Drives the feed dot's quiet-vs-stalled distinction. Keep
   *  the handler trivially cheap (it can fire hundreds of times/sec). */
  onKeepalive?: () => void;
  /** Main's transport-liveness read (an age in ms; 0 = a frame just parsed),
   *  live frames only — a greet replay proves nothing about the socket. */
  onTransportAlive?: (ageMs: number) => void;
  /** Hydra withdrew a book it owned (#1092 coverage retirement): the store
   *  retires the source-owned book instead of rendering it frozen. */
  onOrderbookUnavailable?: (ticker: string) => void;
  /** Latest hydra backpressure level for this connection (0–3). At level
   *  >= 2 hydra suspends orderbook frames entirely (only market + consensus
   *  flow, coalesced) — orderbook-driven UI should show a stale indicator.
   *  Hydra only messages upward transitions; recovery is signalled by
   *  orderbook frames resuming, at which point this fires with 0. */
  onBackpressure?: (level: number) => void;
  /** OddsPapi frames (raw wire messages — the caller's opSnapshotStager
   *  owns staging/commit/rev semantics). Dispatched ONLY on a client
   *  constructed with `subscribeOpOdds: true` (the firehose — DNL-003);
   *  on any other client the frames are dropped + counted, never
   *  dispatched. In shared mode these arrive worker-mediated: live frames
   *  relayed verbatim, snapshot bursts re-framed by the worker (its own
   *  generation namespace) — same stager semantics either way. */
  onOpOdds?: (msg: HydraOpOddsMsg) => void;
  onOpSnapshotBegin?: (msg: HydraOpSnapshotBeginMsg) => void;
  onOpSnapshotEnd?: (msg: HydraOpSnapshotEndMsg) => void;
  /** One served game-state change (or a retirement, `state: null`). */
  onGameState?: (msg: HydraGameStateMsg) => void;
  onGsSnapshotBegin?: (msg: HydraGsSnapshotBeginMsg) => void;
  onGsSnapshotEnd?: (msg: HydraGsSnapshotEndMsg) => void;
  /** Consumer feed filters (Stage C). `onFilterReset` is the ONE atomic reset
   *  per installed generation — fired BEFORE any frame of that generation
   *  reaches `onMarket` (shared mode: the worker emits it; direct mode: this
   *  client does, on its own ack). The receiver drops every market row that
   *  fails `scope` (rowPassesFilter); the replacement snapshot repopulates.
   *  `onFilterAck` is telemetry (health panel / verification) — every ack,
   *  accepted or rejected, on both transports. */
  onFilterReset?: (reset: FencePair & { scope: MarketFilterEffective }) => void;
  onFilterAck?: (ack: HydraFilterAckMsg) => void;
  /** `marketRemoved` (games-axis release gate, hydra review DNL-003): the
   *  engines withdraw an already-delivered row whose identity change makes it
   *  fail the installed filter. The receiver must drop the ticker's row state
   *  — store eviction AND replay/cache paths — for the stamped pair. Fired
   *  only for frames that pass the fence, so `pair` is always the installed
   *  one. */
  onMarketRemoved?: (ticker: string, pair?: FencePair) => void;
}

// ── Constants ────────────────────────────────────────────────────

const PING_INTERVAL = 30_000;
const BASE_RECONNECT_DELAY = 1_000;
const MAX_RECONNECT_DELAY = 30_000;

// ── Client ───────────────────────────────────────────────────────

// ── SharedWorker generation (the wedge escape) ───────────────────
//
// `new SharedWorker(name)` ATTACHES to any existing instance with that name —
// it never replaces one. So when a worker instance goes irrecoverably sick
// (its global scope crashed during an outage storm, say), every recovery path
// re-adopts the corpse: restartShared() spawns "a new worker" that IS the old
// one, a reload attaches to it, a new tab attaches to it. The only escapes
// were closing every tab in the partition or killing the browser process —
// verified live 2026-08-16, where a wedged fleet survived reloads AND a
// browser "close" (background mode kept the process).
//
// The escape: version the instance name with a GENERATION persisted per base
// name. Generation 0 is the bare base name, so the default path — every
// browser that never needed a restart — is byte-identical to before.
//
// The LIFECYCLE BOUNDARY matters as much as the mechanism (DNL-R13-P1: the
// first cut escaped on every restartShared, turning bfcache restores and
// quiet-feed stalls into a generation mint). The states are kept distinct:
//
//   port recovery  — bfcache restore, tab-side stall — reattaches at the
//                    SAME generation, exactly the pre-escape behavior.
//   proven sick    — a fresh attach yields ZERO port messages for
//                    GREET_TIMEOUT_MS. The worker re-broadcasts `__status`
//                    to every new port regardless of market quiet, so total
//                    attach silence is evidence about the WORKER GLOBAL,
//                    not the feed. First timeout retries the current
//                    generation once; a second silent attach escapes
//                    (adopt-or-bump).
//   convergence    — peers observe the generation write via the `storage`
//                    event and migrate even while healthy, so the abandoned
//                    instance loses ALL its ports (bounded double-socket:
//                    storage events deliver in milliseconds, not "whenever
//                    the laggard's own detector happens to fire").
//
// Storage failures degrade to the bare base name in persistence while still
// escaping in memory, never to a crash.
const WORKER_GEN_STORAGE_PREFIX = 'g1xl_worker_gen:';

function readWorkerGeneration(baseName: string): number {
  try {
    const raw = localStorage.getItem(WORKER_GEN_STORAGE_PREFIX + baseName);
    if (raw == null) return 0;
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

function writeWorkerGeneration(baseName: string, gen: number): void {
  try {
    localStorage.setItem(WORKER_GEN_STORAGE_PREFIX + baseName, String(gen));
  } catch { /* storage unavailable — the bare name still works */ }
}

/** Gen 0 MUST be the bare base name: browsers that never restart stay on the
 *  exact instance names prod has always used. */
function decoratedWorkerName(baseName: string, gen: number): string {
  return gen > 0 ? `${baseName}#g${gen}` : baseName;
}

/** A feed URL for a log line: the consumer bearer rides `?token=` on core
 *  URLs, and console output ends up in screenshots and pasted bug reports. */
function redactUrl(url: string): string {
  return url.replace(/([?&]token=)[^&#]+/gi, '$1***');
}

// ── Escape bounds (2026-10-03, the gbuff 20 s generation loop) ───────────
// A tab that cannot process its own port messages in time — drowning in a
// backlog, throttled, frozen — reads every fresh attach as "mute" and, with
// a 10 s greet timeout, retried and escaped every 20 s: Dan's gbuff tab hit
// generation 1,035, dragging every other gbuff tab along through the storage
// event and leaving a live worker instance behind each time. Silence is only
// evidence of a sick WORKER when the tab itself is visible and keeping up:
//   - no stall restart, retry or escape while the document is hidden;
//   - no escape while the tab's own message lag (the worker stamps `at` on
//     `__alive` / `__stats`) says it is behind — re-arm instead;
//   - at most ESCAPE_MAX_PER_PAGE escapes per page per feed, at least
//     ESCAPE_MIN_INTERVAL_MS apart; past that, retries at the SAME
//     generation with a growing greet timeout;
//   - following a peer's escape is rate-limited to one per
//     PEER_MIGRATION_MIN_INTERVAL_MS (a looping peer cannot drag this tab
//     every 20 s), except when our own instance says it retired.
const ESCAPE_MIN_INTERVAL_MS = 5 * 60_000;
/** Migration: wait this long after `__retire` before attaching the new instance. */
const RETIRE_SETTLE_MS = 150;
const ESCAPE_MAX_PER_PAGE = 3;
const PEER_MIGRATION_MIN_INTERVAL_MS = 60_000;
const GREET_TIMEOUT_MAX_MS = 120_000;
/** Default tab-side stall threshold: three missed 5 s worker ticks (see the
 *  `tabStallThresholdMs` field). */
const TAB_STALL_DEFAULT_MS = 15_000;
export const TAB_LAG_LIMIT_MS = 3_000;
const TAB_LAG_SAMPLE_MAX_AGE_MS = 10_000;
/** The tab's own port-message lag, shared by every client in the tab. */
const _tabLag = { lagMs: 0, sampledAtMs: 0 };
function noteTabLag(stampMs: unknown): void {
  if (typeof stampMs !== 'number' || !Number.isFinite(stampMs)) return;
  const now = Date.now();
  _tabLag.lagMs = Math.max(0, now - stampMs);
  _tabLag.sampledAtMs = now;
}
/** Exported for tests: is this tab behind on its own port messages? */
export function tabIsLagging(now = Date.now()): boolean {
  const age = now - _tabLag.sampledAtMs;
  return age >= 0 && age <= TAB_LAG_SAMPLE_MAX_AGE_MS && _tabLag.lagMs > TAB_LAG_LIMIT_MS;
}
/** #1124: the tab's port lag as a number, for the Data pressure card. Same
 *  sample and freshness rule as `tabIsLagging()`; null when there's no fresh one. */
export function getTabLag(now = Date.now()): { lagMs: number; sampledAtMs: number } | null {
  const age = now - _tabLag.sampledAtMs;
  if (_tabLag.sampledAtMs === 0 || age < 0 || age > TAB_LAG_SAMPLE_MAX_AGE_MS) return null;
  return { lagMs: _tabLag.lagMs, sampledAtMs: _tabLag.sampledAtMs };
}
/** Test seam: the lag sample is module-wide (shared by every client in a tab). */
export function _resetTabLagForTests(): void {
  _tabLag.lagMs = 0;
  _tabLag.sampledAtMs = 0;
}
function documentHidden(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden';
}

export class HydraClient {
  private url: string;
  private callbacks: HydraCallbacks;
  // SharedWorker instance name. Each live feed (firehose, edge:nfl, …) MUST use
  // a distinct name so they get separate worker instances / upstream sockets;
  // two clients sharing a name would share one worker (and one URL).
  // `workerName` is the CURRENT effective (generation-decorated) name;
  // `workerBaseName` is the stable identity config supplied.
  private workerName: string;
  private readonly workerBaseName: string;
  private workerGen: number;
  // Proven-sickness detector: armed on EVERY shared attach, cleared by ANY
  // port message (the worker's own greet counts — it is upstream-independent
  // proof the global runs). Fires ⇒ retry current generation once, then
  // escape. See the generation doc block above the class.
  private greetTimeoutTimer: ReturnType<typeof setTimeout> | null = null;
  private greetTimeoutRetriedCurrentGen = false;
  private escapesThisPage = 0;
  private lastEscapeMs = 0;
  private lastMigrationMs = 0;
  private deferredMigration: ReturnType<typeof setTimeout> | null = null;
  private pendingMigrationGen = 0;
  private greetTimeoutMs = 0;
  private lifecycleListener: (() => void) | null = null;
  private freezeListener: (() => void) | null = null;
  private resumeListener: (() => void) | null = null;
  private retireSettleTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly GREET_TIMEOUT_MS = 10_000;
  // Peer-escape observation (`storage` fires only for OTHER tabs' writes).
  private storageListener: ((ev: StorageEvent) => void) | null = null;

  // Direct-WS path
  private ws: WebSocket | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;

  // SharedWorker path
  private worker: SharedWorker | null = null;
  private port: MessagePort | null = null;
  private workerHeartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private stallDetectorTimer: ReturnType<typeof setInterval> | null = null;
  // Liveness ping cadence for the SharedWorker reaper. Worker drops
  // sources stale > 30 s; 10 s here gives 3 attempts before reap.
  private readonly WORKER_HEARTBEAT_INTERVAL_MS = 10_000;
  // Tab-side stall detector: if the port goes quiet for this long
  // while we still think we're connected, the worker has stopped
  // talking to us (port reaped during freeze, worker died, etc).
  // Recovery is stop()+startShared() — same path as a bfcache resume.
  //
  // Liveness is the WORKER's tick, not market frames: the worker posts
  // `__alive` / `__stats` every 5 s while its upstream socket is open, so a
  // quiet-but-live edge (dead hours, a muted ALT edge, a standby) keeps the
  // port warm with no frame at all. The default threshold is three missed
  // ticks (TAB_STALL_DEFAULT_MS). It was 5 s = one tick, and timer jitter
  // tripped it on quiet edges every ~12 min — each false restart re-attached
  // the port, and the retry ledger it armed turned the next hiccup into a
  // generation escape that left the old instance draining the edge with no
  // tab (g1xl-v2#1053 Part 2b, 2026-10-02: five connection sets per edge
  // from one machine). Narrow lane clients still override via the
  // `tabStallThresholdMs` constructor opt (streamrift: liveGameLane passes
  // 45 s, the book lane its own): a sports-filtered lane legitimately hears
  // nothing but the 30 s pong between innings. Check interval 2 s caps
  // detection latency at threshold + interval.
  private readonly tabStallThresholdMs: number;
  private readonly TAB_STALL_CHECK_INTERVAL_MS = 2_000;

  // Cached orderbook needs (shared mode only). When stop()+start()
  // rebuilds the SharedWorker port — bfcache resume, stall recovery —
  // we replay these to the new port without waiting for components to
  // re-mount. Worker also caches per-port as belt-and-suspenders.
  private lastDeclaredNeeds: string[] = [];

  private mode: 'shared' | 'direct' | null = null;
  private stopped = false;
  private _connected = false;
  private _connectedSinceMs = 0;

  /**
   * The ONLY place `_connected` changes, so a connection boundary can never be
   * handled at five sites and missed at the sixth (DNL-001 was exactly that).
   * On a real transition it clears the OP proof-of-service stamp: liveness is a
   * property of the CURRENT transport, and a timestamp that outlives its socket
   * lets a reconnected-but-mute engine look like it is still serving.
   *
   * Idempotent on a repeated same-value status — the SharedWorker re-broadcasts
   * `__status` (greet/replay), and treating those as boundaries would erase a
   * live engine's proof over and over so it could never be promoted.
   */
  private setConnectedState(next: boolean): void {
    if (!next) {
      this.sourceContinuity.reset();
      this.callbacks.onSourceContinuity?.(null);
    }
    if (this._connected === next) return;
    this._connected = next;
    this._lastOpFrameMs = 0;
    this._connectedSinceMs = next ? Date.now() : 0;
    // #1124: every reconnect this tab sees, port rebuilds included.
    if (next) {
      if (this._everConnected) {
        this._connectionResets += 1;
        noteConnectionEvent('reset');
      }
      this._everConnected = true;
    }
  }

  // Latest backpressure level hydra has reported for this connection
  // (0–3). At >= 2 hydra suspends orderbook frames — see HYDRA_API.md
  // "Backpressure warning". Cleared back to 0 when an orderbook frame
  // arrives (the recovery signal — hydra sends no "recovered" message)
  // or when the connection drops (fresh socket = empty buffer = level 0).
  private _backpressureLevel = 0;
  // #1124: when this tab first heard a level above 0 on this socket (wall
  // clock, for display). The V1.0 fleet sends 0 → 1 once and no all-clear.
  private _backpressureSinceMs = 0;
  // #1124 Data pressure readings, all per event.
  private _everConnected = false;
  private _connectionResets = 0;
  private _upstreamEverConnected = false;
  private _upstreamConnected = false;
  private _upstreamReconnects = 0;
  private _resnapshotRequests = 0;
  private readonly snapshotTraceBuffer = new SnapshotTraceBuffer();
  get snapshotTrace(): readonly SnapshotTrace[] { return this.snapshotTraceBuffer.read(); }

  // Stats
  private _marketsReceived = 0;
  private _orderbooksReceived = 0;
  private _lastMessageMs = 0;
  // True while the SharedWorker's greet cache replay is streaming into this
  // port (between __greet_begin and __greet_end). Frames inside the bracket
  // still count toward _marketsReceived (freshness / greet-proof semantics
  // unchanged) but NOT toward _liveMarketFrames.
  private _inGreetReplay = false;
  // Market frames received from a LIVE upstream — greet cache replay
  // excluded. Never reset: consumers (the live game lane's watchdog)
  // snapshot it at a boundary and diff. In direct-WS mode there is no
  // cache replay, so every market frame counts — correct by construction.
  // Fail-open note: a persisted worker from a pre-bracket bundle sends no
  // brackets, so its replay counts as live; acceptable — Vite's
  // content-hashed worker URLs give fresh workers on every deploy.
  private _liveMarketFrames = 0;
  // True after the engine's latest `activeSports` announce was EMPTY — an
  // idle edge (a plane warm spare like he4/he5) that serves no sport and
  // therefore sends no frames. Port silence from such an engine is the
  // expected state, not a stall: the tab-side detector must not churn it
  // through restartShared every threshold window. Cleared by any non-empty
  // announce, so an edge that gets a sport assigned resumes normal watch.
  private _engineIdle = false;
  /** Direct-mode market frames dropped by the fence (stale pair / unstamped). */
  private _marketFramesDropped = 0;
  /** `marketRemoved` frames admitted (games-axis gate) — verification surface. */
  private _marketsRemoved = 0;

  // OddsPapi capability flag (DNL-003, failover rev 2026-08-11). Set on the
  // env-preferred OP source and the OG warm standby; a client without it
  // DROPS oddspapi-channel frames (counted below) instead of dispatching
  // them. Exactly-one-ingestion is enforced a layer up: useHydra gates
  // dispatched frames to the single ACTIVE source, so a capable standby's
  // frames never reach the stager while the preferred source serves.
  private readonly subscribeOpOdds: boolean;
  // Dedicated OP transport (feedConfig `op:0`): this connection speaks ONLY
  // the oddspapi channel — markets/unabated subscribes are skipped in both
  // transports (worker via __init, direct below).
  private readonly opOnly: boolean;
  /** Fixed narrow scopes for dedicated connections (null = broad).
   *  `unabatedGameKeys` is mutable — `addUnabatedGameKeys` widens it so a
   *  later `startShared()` restart's `__init` carries the full union. */
  private readonly marketsSports: string[] | null;
  private unabatedGameKeys: string[] | null;
  private _opFramesDropped = 0;
  /** Direct-mode oddspapi frames the odds-filter link did not admit. */
  private _opFramesFenced = 0;
  /** filterAcks with no channel we speak — dropped, never fenced (D5). */
  private _untaggedFilterAcks = 0;
  // Wall-clock of the last oddspapi-channel frame DISPATCHED by this client
  // (0 = none ever). This is the only per-CHANNEL liveness signal in the
  // codebase: `lastMessageMs` is channel-agnostic and a 30s pong keeps it warm
  // forever, and the worker's stall watchdog deliberately excludes OP frames.
  // Without this, "connected but no longer serving oddspapi" is invisible — a
  // core whose OP lane dies with its socket up looks perfectly healthy to
  // every other mechanism. useHydra's ranked failover reads it to decide both
  // demotion and — critically — whether a better-ranked core has PROVEN it
  // serves the channel before being promoted back into the authority slot.
  private _lastOpFrameMs = 0;

  // Current orderbook-channel subscription state (direct-WS mode only;
  // SharedWorker path delegates this to hydraWorker.ts via __needs).
  private directOrderbookNeeds: Set<string> = new Set();
  /** Direct-WS props opt-in (mirrors the worker's sticky propsWanted). */
  private directPropsWanted = false;
  private directUpstreamOrderbookTickers: Set<string> = new Set();
  private directPendingUnsubscribeTimers: Map<string, ReturnType<typeof setTimeout>> = new Map();
  /** Orderbook unsubscribe grace on the direct path — the same short
   *  flicker window as the worker's aggregator (g1xl-v2#1053 route
   *  transactions): books for a page you left stop within it. */
  private readonly UNSUBSCRIBE_GRACE_MS = ORDERBOOK_UNSUBSCRIBE_GRACE_MS;

  // ── Consumer feed filters (Stage C) ─────────────────────────────
  // Rollout mode, resolved once per client (env is build-time static) and
  // shipped to the SharedWorker on `__init` — the worker never reads env.
  private readonly filterMode: FeedFilterMode;
  // Uppercase wire codes of the sports THIS feed may own (its claim scope);
  // null = unrestricted (the firehose's complement is dynamic, so it passes
  // null and the Phase-2 sports axis is restricted by the tab-side needs
  // publisher instead). Ships to the worker on `__init` as well.
  private readonly feedSports: ReadonlySet<string> | null;
  private readonly sourceEvidence: boolean;
  // Cached needs (shared mode): replayed to a rebuilt port exactly like
  // lastDeclaredNeeds, so a stall recovery / bfcache reattach keeps the same
  // filter without waiting for views to re-declare.
  private lastDeclaredFilterNeeds: FeedNeeds | null = null;
  /** Requested cadence in force for this connection (mode default until the
   *  fleet re-negotiates it — the core floor's standby/sole-source switch). */
  private policyCadenceMs: number;
  /** g1xl-v2#1106 — the ALT tokens an edge is serving (uppercase, sorted),
   *  from the same fleet policy. Only a core floor (`floorAxis`) reads it. */
  private policyFloorOmit: string[] = [];
  // Direct-WS path only — the SharedWorker owns these in shared mode.
  private directFilterAggregator: FeedFilterAggregator<'tab'> | null = null;
  private directFilter: MarketFilter | null = null;
  /** Direct-mode mirror of the worker's floor scope decay (floor-selection-
   *  churn, 2026-10-22): the floor keeps what it carried recently. */
  private readonly directFloorMemory = new FloorScopeMemory();
  private directFloorDecayTimer: ReturnType<typeof setTimeout> | null = null;
  // Floor install settle (Danielle R1 DNL-FSC-001), direct-mode mirror of
  // the worker's: every floor filter change is coalesced into one install,
  // trailing FLOOR_SETTLE_MS from the latest change, bounded by
  // FLOOR_SETTLE_MAX_MS from the first. Markets only — the oddspapi link is
  // updated straight from the aggregator's emit (DNL-FSC-002).
  private directFloorInstallTimer: ReturnType<typeof setTimeout> | null = null;
  private directFloorInstallSinceMs: number | null = null;
  private directFloorInstallSlackMs = 0;
  private readonly directFence = new FilterFence();
  /** True while the connect-time subscribe folds pending needs in: the
   *  aggregator's change callback must update the filter but NOT send a
   *  replace — the connect op that follows carries it. */
  private directSubscribing = false;
  /** First-subscribe deferral (mirror of the worker's): a socket opened
   *  before any view declared waits up to FIRST_NEEDS_WAIT_MS for the
   *  declaration rather than pulling the whole slate broad and replacing it
   *  50 ms later. */
  private deferredMarketsSubscribe: ReturnType<typeof setTimeout> | null = null;
  private static readonly FIRST_NEEDS_WAIT_MS = 300;
  /** Ack-timeout fallback (mirror of the worker's): a filtered subscribe that
   *  gets no filterAck within ACK_TIMEOUT_MS degrades THIS socket to the
   *  legacy subscribe — broad and admitted — instead of dark. Retried on
   *  every new socket; a late ack re-arms the fence. */
  private static readonly ACK_TIMEOUT_MS = 8_000;
  private directAckTimer: ReturnType<typeof setTimeout> | null = null;
  private directLegacyFallback = false;
  /** Part 0 (#1053): last tally — the worker's `__stats` in shared mode, this
   *  socket's own window in direct mode. */
  private _transportStats: TransportStats | null = null;
  private _directTally: { since: number; frames: number; bytes: number; byType: Record<string, number> } = { since: Date.now(), frames: 0, bytes: 0, byType: {} };
  private static readonly DIRECT_STATS_WINDOW_MS = 5_000;
  /** Part 2b (#1053): filter replaces on the direct socket are sent at most
   *  once per window, trailing-edge, newest wins (the worker has the same
   *  limiter — see `sendFilterReplace` there for the incident history). */
  private static readonly FILTER_REPLACE_MIN_MS = 1_000;
  private directLastReplaceMs = 0;
  private directLastReplaceKey: string | null = null;
  private directPendingReplace: ReturnType<typeof setTimeout> | null = null;

  /** Send (or schedule) a filter replace on the direct socket, rate-limited. */
  private sendDirectFilterReplace(next: MarketFilter): void {
    const key = filterKey(next);
    const now = Date.now();
    const due = this.directLastReplaceMs + HydraClient.FILTER_REPLACE_MIN_MS - now;
    const canSend = () => !this.directSubscribing && !this.directLegacyFallback && !this.deferredMarketsSubscribe && !!this.ws && this.ws.readyState === WebSocket.OPEN;
    if (due <= 0 && !this.directPendingReplace) {
      if (key === this.directLastReplaceKey) return;
      if (!canSend()) return;
      this.directLastReplaceKey = key; this.directLastReplaceMs = now;
      this.send(replaceMarketFilterOp(next));
      return;
    }
    if (this.directPendingReplace) return;
    this.directPendingReplace = setTimeout(() => {
      this.directPendingReplace = null;
      const latest = this.directFilter;
      if (!latest || !canSend()) return;
      const k = filterKey(latest);
      if (k === this.directLastReplaceKey) return;
      this.directLastReplaceKey = k; this.directLastReplaceMs = Date.now();
      this.send(replaceMarketFilterOp(latest));
    }, Math.max(due, 0));
  }
  /** D5.4 (direct path, mirror of the worker's `announcedSports`): this
   *  socket's latest `activeSports` announce, raw wire codes, per
   *  connection. With the claim scope it decides ALT-ONLY, which mutes the
   *  markets subscribe until the tab's needs name one of the feed's tokens. */
  private directAnnouncedSports: string[] | null = null;
  /** Direct-mode mirror of the worker's `announcedAxes` — the proposed axes
   *  this connection's announce advertised (g1xl-v2#1053 Part 3). */
  private directAnnouncedAxes: Set<string> | null = null;

  /** Skip the SharedWorker and open a per-window WebSocket (see the opt). */
  private readonly forceDirect: boolean;
  private readonly booksOnly: boolean;
  // ── OddsPapi filter (odds-gamestate-feed-filters Stage C′) ────────
  // On an OP-capable feed with the odds flag on, the oddspapi subscribe
  // carries its own versioned filter, built from the tab's odds facets.
  // Shared mode: the worker runs the lifecycle and tells us its state
  // (`__opLink`). Direct mode: this socket's `directOpLink` does — same
  // module, same rules (D1a included).
  private readonly opFilterOn: boolean;
  private directOpLink: ChannelFilterLink<OpFilter> | null = null;
  private deferredOpSubscribe: ReturnType<typeof setTimeout> | null = null;
  /** The worker's upstream oddspapi filter is installed (shared mode). */
  private workerOpInstalled = false;

  constructor(
    url: string,
    callbacks: HydraCallbacks,
    opts: {
      workerName?: string;
      subscribeOpOdds?: boolean;
      opOnly?: boolean;
      /** Narrow the `markets` channel to these hydra sport codes (live stream
       *  AND snapshot — HYDRA_API.md §"Sport filter"). Dedicated fixed-scope
       *  connections only (the /live game lane); the dashboard fleet stays
       *  broad on purpose (0dc5ea47). WorkerName must encode the scope —
       *  __init is first-writer-wins per worker. */
      marketsSports?: string[];
      /** Narrow the `unabated` channel to these canonicalGameKey filters
       *  (same dedicated-connection rule as marketsSports; widen later with
       *  `addUnabatedGameKeys` — the worker unions additively). */
      unabatedGameKeys?: string[];
      /** Tab-side stall threshold override. Default 5s fits the broad
       *  fleet's traffic; narrow lanes MUST pass ~45s (see the field doc). */
      tabStallThresholdMs?: number;
      /** Override the env-resolved rollout mode (tests). */
      feedFilterMode?: FeedFilterMode;
      /** Sports this feed may own (any case; normalized to wire codes). */
      feedSports?: Iterable<string> | null;
      /**
       * Open a per-window WebSocket even where SharedWorkers exist. For
       * short-lived, on-demand connections only (the Sniper props sidecar,
       * Josh 2026-09-10): a SharedWorker outlives its last port — the browser
       * keeps it while the page lives — so its upstream socket would keep
       * streaming after `stop()`. A direct socket closes on `stop()`. Default
       * false: every existing connection keeps the shared transport.
       */
      forceDirect?: boolean;
      /**
       * A socket that carries ORDER BOOKS and nothing else — never `markets`,
       * never `unabated` (the watched-game book lane). Bulk market snapshots
       * share a socket's backpressure state with its books: a whole-label
       * replacement snapshot is what flips an edge consumer to level 1 and
       * coalesces every book on that socket to 250–1000 ms. A socket that
       * never asks for markets can never be handed one. Always direct, and the
       * markets filter machinery is off for it (there is nothing to filter).
       */
      booksOnly?: boolean;
      sourceEvidence?: boolean;
    } = {},
  ) {
    this.url = url;
    this.callbacks = callbacks;
    this.booksOnly = opts.booksOnly ?? false;
    this.sourceEvidence = opts.sourceEvidence === true;
    const resolvedFilterMode = opts.feedFilterMode ?? resolveFeedFilterMode();
    this.filterMode = this.booksOnly ? { ...resolvedFilterMode, enabled: false } : resolvedFilterMode;
    this.policyCadenceMs = this.filterMode.cadenceMs ?? 250;
    this.feedSports = opts.feedSports ? new Set([...opts.feedSports].map((s) => s.toUpperCase())) : null;
    this.workerBaseName = opts.workerName ?? 'hydra-shared';
    this.marketsSports = opts.marketsSports ?? null;
    this.unabatedGameKeys = opts.unabatedGameKeys ?? null;
    // Join the fleet's LATEST generation at construction: if another tab
    // already escaped a wedged instance, a fresh page load must land on the
    // healthy replacement, not re-adopt the corpse by its old name.
    this.workerGen = readWorkerGeneration(this.workerBaseName);
    this.workerName = decoratedWorkerName(this.workerBaseName, this.workerGen);
    this.subscribeOpOdds = opts.subscribeOpOdds ?? false;
    this.opOnly = opts.opOnly ?? false;
    this.tabStallThresholdMs = opts.tabStallThresholdMs ?? TAB_STALL_DEFAULT_MS;
    this.forceDirect = (opts.forceDirect ?? false) || this.booksOnly;
    this.opFilterOn = this.filterMode.opFilter === true && this.subscribeOpOdds;
  }

  /** The markets filter runs on this connection (flag on, not an OP-only
   *  transport). */
  private get marketsFilterOn(): boolean {
    return this.filterMode.enabled && !this.opOnly;
  }

  get connected(): boolean { return this._connected; }
  /** Last transport tally for this feed (see TransportStats); null before
   *  the first tick. Direct mode rolls its own 5 s window on read. */
  get transportStats(): TransportStats | null {
    if (this.mode === 'direct') {
      const now = Date.now();
      const w = this._directTally;
      if (now - w.since >= HydraClient.DIRECT_STATS_WINDOW_MS) {
        this._transportStats = {
          at: now, intervalMs: now - w.since, frames: w.frames, bytes: w.bytes, byType: { ...w.byType }, ports: 1,
          filterGeneration: this.directFence.pair?.filterGeneration ?? null, legacyFallback: this.directLegacyFallback,
          untaggedFilterAcks: this._untaggedFilterAcks,
          opFilter: this.directOpLink
            ? { mode: this.directOpLink.mode, filterGeneration: this.directOpLink?.fence.pair?.filterGeneration ?? null, ...this.directOpLink.counters }
            : null,
        };
        this._directTally = { since: now, frames: 0, bytes: 0, byType: {} };
      }
    }
    return this._transportStats;
  }
  get marketsReceived(): number { return this._marketsReceived; }
  /** Market frames from LIVE upstream only — SharedWorker greet cache
   *  replay excluded. THE liveness signal for "is the engine actually
   *  serving?", because a warm worker's replay makes `marketsReceived`
   *  non-zero instantly against a dead upstream. Monotonic, never reset —
   *  snapshot and diff across a boundary. */
  get liveMarketFramesReceived(): number { return this._liveMarketFrames; }
  get marketFramesDropped(): number { return this._marketFramesDropped; }
  get marketsRemoved(): number { return this._marketsRemoved; }
  get orderbooksReceived(): number { return this._orderbooksReceived; }
  get lastMessageMs(): number { return this._lastMessageMs; }
  get backpressureLevel(): number { return this._backpressureLevel; }
  /** #1124: when the level first rose above 0 on this socket; null at 0. */
  get backpressureSinceMs(): number | null { return this._backpressureSinceMs > 0 ? this._backpressureSinceMs : null; }
  /** #1124: reconnects this tab saw after the first connect, port rebuilds included. */
  get connectionResets(): number { return this._connectionResets; }
  /** #1124: the upstream socket itself coming back (worker status or direct open). */
  get upstreamReconnects(): number { return this._upstreamReconnects; }
  /** #1124: resnapshot requests this client actually sent. */
  get resnapshotRequests(): number { return this._resnapshotRequests; }
  /** #1124: how many books this tab routed to this client. */
  get declaredBookNeeds(): number { return this.lastDeclaredNeeds.length; }
  /** #1124: windows attached to this feed's worker, from its last tally. Read
   *  without rolling the direct-mode window; null in direct mode. */
  get sharedPorts(): number | null { return this.mode === 'shared' ? this._transportStats?.ports ?? null : null; }
  /** oddspapi-channel frames dropped because this client is not the OP
   *  ingestion authority (`subscribeOpOdds: false`) — the DNL-003 counted
   *  diagnostic. */
  get opFramesDropped(): number { return this._opFramesDropped; }
  /** Direct-mode oddspapi frames dropped by the odds-filter fence (stale
   *  pair, pre-ack, or stamped on a legacy socket). Diagnostics + tests. */
  get opFramesFenced(): number { return this._opFramesFenced; }
  /** filterAcks dropped for carrying no channel we speak (directory-edgelike-cores D5). */
  get untaggedFilterAcks(): number { return this._untaggedFilterAcks; }
  /** The direct socket's oddspapi link (null with the odds filter off, or in
   *  shared mode). Diagnostics + tests. */
  get directOddsLink(): ChannelFilterLink<OpFilter> | null { return this.directOpLink; }
  /** Wall-clock of the last oddspapi frame this client dispatched ON THE
   *  CURRENT CONNECTION; 0 = none since the last connection boundary.
   *  Per-channel liveness for the ranked OP failover — see `_lastOpFrameMs`. */
  get lastOpFrameMs(): number { return this._lastOpFrameMs; }
  /** Wall-clock of the current connection's establishment; 0 while down. Anchors
   *  the not-yet-delivered grace so a reconnect gets a fresh window instead of
   *  inheriting a tenure clock that already expired. */
  get connectedSinceMs(): number { return this._connectedSinceMs; }

  /** Transport this client resolved to: 'shared' (SharedWorker — one upstream WS
   *  shared across all tabs) or 'direct' (a per-window WS, the fallback where
   *  `type:'module'` SharedWorkers are unsupported, e.g. Safari); 'connecting'
   *  before start / after stop. Surfaced in G1 Health so the shared-vs-direct
   *  question (the "new connection every reload/window" symptom) is observable. */
  get transport(): 'shared' | 'direct' | 'connecting' {
    return this.mode ?? 'connecting';
  }

  /** #1124: the upstream socket's own state, apart from this tab's port. A
   *  direct socket closing reports false through `onclose`. */
  private noteUpstream(connected: boolean): void {
    if (connected && !this._upstreamConnected && this._upstreamEverConnected) this._upstreamReconnects += 1;
    if (connected) this._upstreamEverConnected = true;
    this._upstreamConnected = connected;
  }

  /** `workerSinceMs`: the worker's time it first heard this socket's level
   *  (`__bpSinceMs` on a greet replay), so a re-greeted window keeps hydra's
   *  time. Same browser wall clock; one later than now falls back to now. */
  private setBackpressureLevel(level: number, workerSinceMs?: unknown): void {
    if (level === this._backpressureLevel) return;
    if (level <= 0) this._backpressureSinceMs = 0;
    else if (this._backpressureLevel <= 0) {
      const now = Date.now();
      this._backpressureSinceMs = typeof workerSinceMs === 'number' && workerSinceMs > 0 && workerSinceMs <= now ? workerSinceMs : now;
    }
    this._backpressureLevel = level;
    this.callbacks.onBackpressure?.(level);
  }

  /** Declare the set of tickers this view needs orderbook data for. In
   *  SharedWorker mode, forwarded to the worker which unions across all
   *  tabs. In direct-WS mode, reconciled locally against the current
   *  upstream subscription with a 30 s grace on removals. */
  setOrderbookNeeds(tickers: string[], _coalescible?: string[]): void {
    // `_coalescible` is the gateway's hint (board cells vs ladders); an edge
    // socket's book cadence is connection-wide, so the fleet stays per-delta.
    // Needs belong to the client, not its current transport. Fleet registration
    // can precede start(), and recovery can switch shared/direct transports.
    this.lastDeclaredNeeds = tickers.slice();
    this.directOrderbookNeeds = new Set(tickers);
    if (this.mode === 'shared') {
      // Cache before posting so a future startShared() (bfcache resume,
      // stall recovery) can replay the same needs without depending on
      // component remounts.
      if (this.port) {
        try {
          this.port.postMessage({ type: '__needs', channel: 'orderbooks', tickers });
        } catch { /* port may be closing — next mount will re-declare */ }
      }
      return;
    }
    // Direct-WS: this client is the sole consumer, so needs == desired.
    this.reconcileDirectOrderbookSubscriptions();
  }

  /** Widen the `unabated` scope of a DEDICATED narrow connection with more
   *  game keys (additive — hydra unions per-channel filters). Merges into
   *  the local list so any later `startShared()` restart's `__init` carries
   *  the full union; shared mode posts `__scope` (the worker re-issues the
   *  subscribe+snapshot for genuinely-new keys only), direct mode sends the
   *  additive pair itself. No-op on a broad client (no filter to widen). */
  addUnabatedGameKeys(keys: string[]): void {
    // #1191: Core retired Unabated — the channel is never subscribed, so a
    // wider scope has nothing to widen. The list is still kept (and carried
    // on `__init`) so the live game lane's bookkeeping stays honest.
    if (this.unabatedGameKeys == null || keys.length === 0) return;
    const merged = new Set(this.unabatedGameKeys);
    for (const k of keys) merged.add(k);
    this.unabatedGameKeys = [...merged];
  }

  /** Declare this tab's aggregated feed-filter needs (which normally-excluded
   *  classes/types it renders; Phase 2: which sports). Shared mode forwards to
   *  the worker, which unions across every attached tab and re-sends the
   *  complete filter (replace semantics) when the union changes. Direct mode
   *  aggregates locally and re-sends on this socket. Inert when the rollout
   *  flag is off. */
  setMarketFilterNeeds(needs: FeedNeeds | null): void {
    // The same declaration carries the markets axes and the odds facets; a
    // connection that runs neither filter has nothing to do with it.
    if (!this.marketsFilterOn && !this.opFilterOn) return;
    // Cached first: a client that has not started yet (or is between
    // transports) replays this on startShared / the direct connect.
    this.lastDeclaredFilterNeeds = needs;
    if (this.mode === 'shared') {
      if (this.port) {
        try {
          this.port.postMessage({ type: '__filter_needs', needs: serializeNeeds(needs) });
        } catch { /* port may be closing — next mount will re-declare */ }
      }
    } else if (this.mode === 'direct') {
      this.ensureDirectFilterAggregator().setNeeds('tab', needs);
      // Every declaration restarts the floor settle, emitted or held
      // (DNL-FSC-001): a navigation's later arrivals land in one install.
      if (this.marketsFilterOn && this.directFloorSettles()) this.requestDirectFloorInstall();
      if (this.marketsFilterOn && this.deferredMarketsSubscribe) {
        // The first declaration IS the first subscribe.
        clearTimeout(this.deferredMarketsSubscribe);
        this.deferredMarketsSubscribe = null;
        this.subscribeMarketsChannel();
      }
      if (this.deferredOpSubscribe) this.subscribeDirectOpNow();
    }
  }

  /** Connection-wide policy (the two-tier model): the requested cadence
   *  (the core floor runs 1000 as the warm standby and 250 while it is the
   *  only source for a viewed sport) and, for the floor, the ALT tokens an
   *  edge is serving (#1106 — `floorOmitSports`; absent = none). Re-sends
   *  the complete filter when either changes. Inert with the flag off. */
  setMarketFilterPolicy(policy: { cadenceMs: number; floorOmitSports?: readonly string[] }): void {
    if (!this.filterMode.enabled || this.opOnly) return;
    const cadenceMs = Number.isFinite(policy.cadenceMs) ? policy.cadenceMs : this.policyCadenceMs;
    const omit = [...new Set((policy.floorOmitSports ?? []).map((s) => s.trim().toUpperCase()).filter(Boolean))].sort();
    if (cadenceMs === this.policyCadenceMs && omit.join(',') === this.policyFloorOmit.join(',')) return;
    this.policyCadenceMs = cadenceMs;
    this.policyFloorOmit = omit;
    if (this.mode === 'shared') {
      if (this.port) {
        try { this.port.postMessage({ type: '__filter_policy', cadenceMs, floorOmitSports: omit }); } catch { /* port closing */ }
      }
      return;
    }
    if (this.mode === 'direct' && this.directFilterAggregator) {
      if (this.directFloorSettles()) { this.requestDirectFloorInstall(); return; }
      const next = this.buildDirectFilter(this.directFilterAggregator.emitted());
      if (this.directFilter && filterKey(this.directFilter) === filterKey(next)) return;
      this.directFilter = next;
      this.sendDirectFilterReplace(next);
    }
  }

  /** The filter currently in force on the direct socket (null = legacy /
   *  shared mode / flag off). Diagnostics + tests. */
  get directMarketFilter(): MarketFilter | null {
    return this.directFilter;
  }

  /** The direct socket's installed fencing pair (null until its ack). */
  get directFencePair(): FencePair | null {
    return this.directFence.pair;
  }

  /** True while the direct socket runs the legacy subscribe because its
   *  engine never acked the filter (diagnostics + tests). */
  get directLegacyFallbackActive(): boolean {
    return this.directLegacyFallback;
  }

  /** OddsPapi liveness on a FILTERED connection (odds-gamestate-feed-
   *  filters). The OP ladder demotes a member silent for OP_SILENCE_MS
   *  (useHydra) — right for the unfiltered firehose, which is never quiet,
   *  wrong for a narrow scope (one quiet game's moneyline). So, like the
   *  markets channel's rule for an ack'd filtered socket, the install and
   *  then the socket's keepalives count as proof the channel is served:
   *  the ack proves the engine runs the filter, the scoped burst follows it,
   *  and a dead socket still drops the member at once. */
  private noteOpAlive(): void {
    if (this._connected) this._lastOpFrameMs = Date.now();
  }

  private clearDirectAckTimer(): void {
    if (this.directAckTimer) { clearTimeout(this.directAckTimer); this.directAckTimer = null; }
  }

  private degradeDirectToLegacy(reason: string): void {
    if (this.directLegacyFallback || this.directFence.pair) return;
    this.directLegacyFallback = true;
    console.warn(`[Hydra] markets filter unsupported on ${this.url} (${reason}) — falling back to the legacy subscribe on this socket`);
    for (const op of connectMarketOps(null, this.directPropsWanted, this.marketsSports)) this.send(op);
  }

  /** The complete wire filter for the direct socket: the union's filter,
   *  then the ALT-only mute (D5.4) from this connection's announce. */
  private buildDirectFilter(live: AggregatedNeeds, decaySlackMs = 0): MarketFilter {
    let needs = live;
    if (this.filterMode.floorAxis) {
      needs = this.directFloorMemory.widen(live, Date.now(), decaySlackMs);
      this.armDirectFloorDecay();
    }
    const base = buildMarketFilter(needs, {
      sportsAxis: this.filterMode.sportsAxis,
      gamesAxis: this.filterMode.gamesAxis,
      tickersAxis: this.filterMode.tickersAxis,
      feedSports: this.feedSports,
      cadenceMs: this.policyCadenceMs,
      engineAxes: this.directAnnouncedAxes,
      floorAxis: this.filterMode.floorAxis,
      floorOmitSports: this.policyFloorOmit,
    });
    // streamrift's dedicated single-game lanes (liveGameLane) pin the
    // connection to their sport: exactly the filter they had before per-edge
    // scope existed — no announce intersection, no ALT mute.
    if (this.marketsSports && this.marketsSports.length > 0) return this.pinLaneMarketsSports(base);
    // Per-edge scope (mirror of the worker): union ∩ what this engine serves.
    const announced = this.directAnnouncedSports && this.directAnnouncedSports.length > 0
      ? new Set(this.directAnnouncedSports.map((s) => s.toUpperCase()))
      : this.feedSports;
    const scoped = this.filterMode.floorAxis ? base : scopeMarketFilter(base, announced, needs, { tickersAxis: this.filterMode.tickersAxis, gamesAxis: this.filterMode.gamesAxis });
    return applyAltOnlyMute(scoped, needs, altOnlyFeedTokens(this.directAnnouncedSports, this.feedSports));
  }

  /** Is the floor settle in force on this socket? Only once a filtered
   *  subscribe is already on the wire (the connect-time subscribe is never
   *  delayed), and never while folding needs into that subscribe. */
  private directFloorSettles(): boolean {
    return this.mode === 'direct' && this.filterMode.enabled && this.filterMode.floorAxis === true && !this.opOnly
      && !this.directLegacyFallback && !this.deferredMarketsSubscribe && !this.directSubscribing
      && this.ws != null && this.ws.readyState === WebSocket.OPEN && this.directLastReplaceKey != null;
  }

  private requestDirectFloorInstall(slackMs = 0): void {
    const now = Date.now();
    this.directFloorInstallSinceMs ??= now;
    this.directFloorInstallSlackMs = Math.max(this.directFloorInstallSlackMs, slackMs);
    if (this.directFloorInstallTimer) clearTimeout(this.directFloorInstallTimer);
    const at = Math.min(now + FLOOR_SETTLE_MS, this.directFloorInstallSinceMs + FLOOR_SETTLE_MAX_MS);
    this.directFloorInstallTimer = setTimeout(() => this.installDirectFloorNow(), Math.max(0, at - now));
  }

  private cancelDirectFloorInstall(): void {
    if (this.directFloorInstallTimer) { clearTimeout(this.directFloorInstallTimer); this.directFloorInstallTimer = null; }
    this.directFloorInstallSinceMs = null;
    this.directFloorInstallSlackMs = 0;
  }

  private installDirectFloorNow(): void {
    this.directFloorInstallTimer = null;
    // A change still inside the aggregator's flicker hold has not emitted:
    // wait for it (its emit restarts the settle), unless the bound is reached.
    const since = this.directFloorInstallSinceMs ?? Date.now();
    if (this.directFilterAggregator?.holding && Date.now() < since + FLOOR_SETTLE_MAX_MS) {
      const at = Math.min(Date.now() + FLOOR_SETTLE_MS, since + FLOOR_SETTLE_MAX_MS);
      this.directFloorInstallTimer = setTimeout(() => this.installDirectFloorNow(), Math.max(0, at - Date.now()));
      return;
    }
    this.directFloorInstallSinceMs = null;
    const slack = this.directFloorInstallSlackMs;
    this.directFloorInstallSlackMs = 0;
    if (this.mode !== 'direct' || !this.filterMode.enabled || this.opOnly || !this.directFilterAggregator) return;
    const next = this.buildDirectFilter(this.directFilterAggregator.emitted(), slack);
    if (this.directFilter && filterKey(this.directFilter) === filterKey(next)) return;
    this.directFilter = next;
    this.sendDirectFilterReplace(next);
  }

  private armDirectFloorDecay(): void {
    if (this.directFloorDecayTimer) { clearTimeout(this.directFloorDecayTimer); this.directFloorDecayTimer = null; }
    const at = this.directFloorMemory.nextExpiryMs();
    if (at == null) return;
    this.directFloorDecayTimer = setTimeout(() => { this.directFloorDecayTimer = null; this.refreshDirectFilterOnFloorDecay(); }, Math.max(0, at - Date.now()));
  }

  /** A remembered sport / class left the floor's grace: narrow now, from the
   *  EMITTED union, one replacement op (mirror of the worker). */
  private refreshDirectFilterOnFloorDecay(): void {
    if (this.mode !== 'direct' || !this.filterMode.enabled || !this.filterMode.floorAxis || this.opOnly || !this.directFilterAggregator) return;
    this.requestDirectFloorInstall(FLOOR_SETTLE_MS);
  }

  /** An announce landed on the direct socket: re-derive the filter from the
   *  unchanged needs and replace it on the wire if it moved (one op — never
   *  a clear-then-redeclare). */
  private refreshDirectFilterOnAnnounce(): void {
    if (this.mode !== 'direct' || !this.filterMode.enabled || this.opOnly || !this.directFilterAggregator) return;
    if (this.directFloorSettles()) { this.requestDirectFloorInstall(); return; }
    // From the EMITTED union: an announce must not leak a held transaction.
    const next = this.buildDirectFilter(this.directFilterAggregator.emitted());
    if (this.directFilter && filterKey(this.directFilter) === filterKey(next)) return;
    this.directFilter = next;
    this.sendDirectFilterReplace(next);
  }

  private ensureDirectFilterAggregator(): FeedFilterAggregator<'tab'> {
    if (this.directFilterAggregator) return this.directFilterAggregator;
    this.directFilterAggregator = new FeedFilterAggregator<'tab'>((needs) => {
      // The odds facets ride the same union and the same hold.
      if (this.directOpLink && !this.deferredOpSubscribe) this.directOpLink.update(buildOpFilter(needs.odds));
      if (!this.marketsFilterOn) return;
      // The floor coalesces its install (DNL-FSC-001); the connect-time
      // subscribe and every other feed take the immediate path below.
      if (this.directFloorSettles()) { this.requestDirectFloorInstall(); return; }
      const next = this.buildDirectFilter(needs);
      if (this.directFilter && filterKey(this.directFilter) === filterKey(next)) return;
      this.directFilter = next;
      // Complete replacement on the live socket; a closed socket picks the
      // current filter up on its next onopen (connect-time carry). A
      // degraded (legacy-fallback) socket picks it up on its next connect.
      this.sendDirectFilterReplace(next);
    });
    // Seed the connection's filter from empty needs (defaults) synchronously
    // so the FIRST subscribe already carries it — never a legacy connect
    // followed by a narrowing replace.
    this.directFilter = this.buildDirectFilter(this.directFilterAggregator.current());
    return this.directFilterAggregator;
  }

  /** Dedicated single-game lanes remain fixed to their sport even when the
   * versioned consumer filter is enabled. The ordinary dashboard never sets
   * `marketsSports`, so its dynamic sports-axis behavior is unchanged. */
  private pinLaneMarketsSports(filter: MarketFilter): MarketFilter {
    return this.marketsSports && this.marketsSports.length > 0
      ? { ...filter, sports: [...this.marketsSports] }
      : filter;
  }

  start(): void {
    this.stopped = false;
    if (!this.forceDirect && typeof SharedWorker !== 'undefined') {
      if (this.startShared()) return;
    }
    this.connect();
  }

  /** Re-request a markets snapshot from upstream. Used by `useHydra` when
   *  the tab returns from background (visibilitychange) or the watchdog
   *  detects a silent stall — both cases where the WS thinks it's
   *  connected but `lastMessageMs` has gone quiet. In SharedWorker mode
   *  delegates to the worker, which forwards to upstream; cache entries
   *  get overwritten as frames stream in. */
  private noteResnapshotRequest(): void {
    this._resnapshotRequests += 1;
    noteConnectionEvent('resnapshot');
  }

  requestResnapshot(reason: SnapshotReason = 'unspecified'): void {
    // Dedicated odds transports never subscribe to markets, including recovery.
    if (this.opOnly) return;
    if (this.mode === 'shared') {
      if (this.port) {
        try {
          this.port.postMessage({ type: '__resnapshot', reason });
          this.noteResnapshotRequest();
        } catch { /* port may be closing — next mount will resume */ }
      }
      return;
    }
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.send(this.filterMode.enabled
        ? { op: 'snapshot', channel: 'markets' }
        : marketsSnapshotOp(this.marketsSports), reason);
      this.noteResnapshotRequest();
    }
    // If not connected, the next reconnect's onopen will snapshot anyway,
    // so no extra work needed here.
  }

  /** A props surface opened: tell the shared worker to stop discarding prop
   *  market frames (sticky for the worker's lifetime) and backfill via a
   *  markets resnapshot. Direct-WS mode lifts its engine-side prop exclusion
   *  too. Dedicated odds transports never opt into market frames. */
  requestPropsFeed(): void {
    if (this.opOnly) return;
    if (this.mode === 'shared') {
      if (this.port) {
        try {
          this.port.postMessage({ type: '__wantProps' });
        } catch { /* port may be closing — next mount will resume */ }
      }
      return;
    }
    // Direct-WS: the engine-side excludeMarketTypes filter applies here too
    // (connectMarketOps default), so lift it the same way — resubscribe
    // without the field + snapshot. Sticky across reconnects.
    this.directPropsWanted = true;
    // Flag on: the versioned filter governs this socket (never-mix) and the
    // props surface's useFeedNeeds declaration is what widens it.
    if (this.filterMode.enabled) return;
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      for (const op of connectMarketOps(null, true, this.marketsSports)) this.send(op);
    }
  }

  /** Which games' OddsPapi frames THIS TAB wants from the shared OP worker:
   *  `null` = the whole channel (a board), a list = only those game keys (a
   *  trader on one game). The worker filters its fan-out per port, so a tab on
   *  one game stops cloning, parsing and ingesting the whole slate's ~50-market
   *  frames — and gets a fresh scoped burst on every change, which both trims
   *  its store when narrowing and refills it when widening back. Remembered so
   *  a worker restart re-declares it. Direct-WS fallback keeps the whole
   *  channel (one tab, no clone fan-out to save). */
  private opScopeGames: string[] | null = null;
  setOpScope(games: readonly string[] | null): void {
    if (!this.subscribeOpOdds) return;
    const next = games == null ? null : [...new Set(games)].sort();
    const prev = this.opScopeGames;
    if (
      (prev == null && next == null) ||
      (prev != null && next != null && prev.length === next.length && prev.every((k, i) => k === next[i]))
    ) return;
    this.opScopeGames = next;
    if (this.mode === 'shared' && this.port) {
      try { this.port.postMessage({ type: '__op_scope', games: next }); } catch { /* port closing — re-declared on restart */ }
    }
  }

  /** Re-request an `oddspapi` snapshot burst — the stager's bounded-backoff
   *  recovery path (truncated burst, unknown epoch). No-op on a client that
   *  is not the OP ingestion authority. Shared mode delegates to the worker
   *  (which throttles duplicate requests across tabs); direct mode sends
   *  the snapshot op itself — a disconnected socket needs nothing extra,
   *  the reconnect onopen re-snapshots every channel. */
  requestOpResnapshot(): void {
    if (!this.subscribeOpOdds) return;
    if (this.mode === 'shared') {
      if (this.port) {
        try {
          this.port.postMessage({ type: '__op_resnapshot' });
          this.noteResnapshotRequest();
        } catch { /* port may be closing — reconnect path re-snapshots */ }
      }
      return;
    }
    // A filtered socket before its ack: the install's own scoped burst is
    // on its way (the link answers false until then).
    if (this.directOpLink && !this.directOpLink.canRequestSnapshot) return;
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.send({ op: 'snapshot', channel: 'oddspapi' }, 'odds-recovery');
      this.noteResnapshotRequest();
    }
  }

  /** Force a full WS reconnect — used when the freshness signal is
   *  yellow AND the user took an explicit action (chip click, button
   *  press) that says "I want fresh data NOW." Recovers from silently
   *  dead sockets where requestResnapshot() over the same dead socket
   *  is itself a lost message.
   *
   *  Shared mode: posts __reconnect_upstream so the worker rebuilds
   *  the upstream WS for every attached tab. Direct mode: stops then
   *  immediately restarts the per-tab WS. Both paths bypass any
   *  staleness gate — the caller already decided the feed is stale. */
  forceReconnect(): void {
    if (this.mode === 'shared') {
      if (this.port) {
        try {
          this.port.postMessage({ type: '__reconnect_upstream' });
        } catch { /* port may be closing — next mount will resume */ }
      }
      return;
    }
    // Direct mode: tear down and immediately re-open. clearTimers +
    // explicit close keeps the existing onclose path quiet enough that
    // we don't double-schedule a reconnect; the new connect() lands
    // before the backoff timer would have.
    this.clearTimers();
    if (this.ws) {
      try { this.ws.close(); } catch { /* ignore */ }
      this.ws = null;
    }
    this.setConnectedState(false);
    this.setBackpressureLevel(0);
    this.callbacks.onStatus(false);
    this.connect();
  }

  stop(): void {
    this.stopped = true;
    if (this.mode === 'shared') {
      this.stopWorkerHeartbeat();
      this.stopStallDetector();
      this.clearGreetTimeout();
      this.removeStorageListener();
      this.removeLifecycleListeners();
      if (this.deferredMigration) { clearTimeout(this.deferredMigration); this.deferredMigration = null; }
      if (this.retireSettleTimer) { clearTimeout(this.retireSettleTimer); this.retireSettleTimer = null; }
      if (this.port) {
        try { this.port.postMessage({ type: '__bye' }); } catch { /* port may be closed */ }
        try { this.port.close(); } catch { /* ignore */ }
        this.port = null;
      }
      this.worker = null;
    } else {
      this.clearTimers();
      if (this.directFloorDecayTimer) { clearTimeout(this.directFloorDecayTimer); this.directFloorDecayTimer = null; }
      this.cancelDirectFloorInstall();
      if (this.ws) {
        this.ws.close();
        this.ws = null;
      }
    }
    this.mode = null;
    this.setConnectedState(false);
    this.setBackpressureLevel(0);
    this.callbacks.onStatus(false);
  }

  // ── SharedWorker path ──────────────────────────────────────────

  private startShared(): boolean {
    try {
      this.worker = new SharedWorker(
        new URL('../workers/hydraWorker.ts', import.meta.url),
        { type: 'module', name: this.workerName },
      );
      this.port = this.worker.port;
      const ownedPort = this.port;
      this.port.onmessage = (ev: MessageEvent) => {
        if (this.port !== ownedPort) return; // queued message from a retired attachment
        const msg = ev.data as HydraServerMsg | WorkerControlMsg | null;
        if (!msg || typeof msg !== 'object') return;
        if (msg.type === '__snapshotTrace') {
          if (msg.entries) this.snapshotTraceBuffer.replace(msg.entries);
          if (msg.entry) this.snapshotTraceBuffer.add(msg.entry);
          return;
        }
        this._lastMessageMs = Date.now();
        // Any port message is proof the worker global runs — the sickness
        // detector stands down and the retry ledger resets.
        this.clearGreetTimeout();
        this.greetTimeoutRetriedCurrentGen = false;
        this.greetTimeoutMs = 0; // the backoff ends the moment the worker answers
        if (msg.type === '__status') {
          this.noteUpstream(!!msg.connected);
          this.setConnectedState(!!msg.connected);
          // A status flip means the upstream socket changed — any prior
          // backpressure level belonged to the old socket. (If hydra still
          // reports one on this socket, at any level, the worker's greet()
          // replays it right after, with the time it first heard it.)
          this.setBackpressureLevel(0);
          this.callbacks.onStatus(this._connected);
          return;
        }
        if (msg.type === '__greet_begin') { this._inGreetReplay = true; return; }
        if (msg.type === '__greet_end') { this._inGreetReplay = false; return; }
        if (msg.type === '__alive') {
          noteTabLag((msg as { at?: unknown }).at);
          // A filtered oddspapi socket with an installed generation may be
          // legitimately silent (a quiet scope) — its liveness is the
          // socket's, as on a filtered markets connection (see noteOpAlive).
          if (this.workerOpInstalled) this.noteOpAlive();
          return;
        } // liveness — _lastMessageMs was bumped above
        if (msg.type === '__opLink') {
          const installed = (msg as WorkerOpLinkMsg).installed === true;
          const fresh = installed && !this.workerOpInstalled;
          this.workerOpInstalled = installed;
          if (fresh) this.noteOpAlive();
          return;
        }
        if ((msg as { type?: string }).type === '__retired') {
          // Our instance closed its upstream because a newer generation of
          // this feed exists: move now (not rate-limited — nothing serves us here).
          const gen = Number((msg as { gen?: unknown }).gen);
          if (Number.isFinite(gen)) this.migrateToGeneration(gen, 'instance retired', true);
          return;
        }
        if (msg.type === '__stats') {
          noteTabLag((msg as { at?: unknown }).at);
          const { type: _t, connected: _c, ...stats } = msg;
          void _t; void _c;
          this._transportStats = stats;
          return;
        }
        if (msg.type === '__op_alive') {
          // The worker suppressed an OP frame outside this tab's game scope.
          // The CHANNEL is serving; this tab just didn't want that game. Keep
          // the per-channel liveness warm, or a trader parked on one quiet
          // pregame game would read as a dead odds lane — demoting a healthy
          // authority and greying a marker that is in fact current.
          this._lastOpFrameMs = Date.now();
          return;
        }
        if (msg.type === '__filterReset') {
          // The worker already fenced the upstream; this is the atomic
          // reset for the tab's stores, ordered by the port ahead of every
          // post-fence frame (worker: reset broadcast precedes forwarding).
          this.callbacks.onFilterReset?.({
            socketIncarnation: msg.socketIncarnation,
            filterGeneration: msg.filterGeneration,
            scope: msg.scope,
          });
          return;
        }
        const transfer = (msg as HydraServerMsg & { __g1SourceAge?: SourceAgeTransfer }).__g1SourceAge;
        // A replayed frame: inside the worker's greet bracket (streamrift) or
        // stamped `__g1Replay` by the worker (main) — the two agree on every
        // greet; the stamp also covers a replay outside the bracket.
        const replay = this._inGreetReplay || (msg as { __g1Replay?: boolean }).__g1Replay === true;
        this.handleMessage(msg, transfer ? receiveSourceAge(transfer) : undefined, replay);
      };
      this.port.start();
      // subscribeOpOdds / opOnly ride __init so the worker knows whether ITS
      // upstream should subscribe the oddspapi channel, and whether it is a
      // dedicated OP transport (skip markets/unabated entirely) — feed-unique
      // workerName keeps the flags coherent across every tab of one feed.
      this.port.postMessage({
        type: '__init', url: this.url, subscribeOpOdds: this.subscribeOpOdds,
        opOnly: this.opOnly,
        // Narrow-scope flags for dedicated connections (live game lane).
        // First-writer-wins per workerName, like opOnly — the workerName
        // encodes the scope, so every tab of one lane passes the same values.
        ...(this.marketsSports ? { marketsSports: this.marketsSports } : {}),
        ...(this.unabatedGameKeys ? { unabatedGameKeys: this.unabatedGameKeys } : {}),
        // Consumer feed filters (Stage C): the rollout mode + this feed's
        // claim scope latch per worker name exactly like opOnly (all tabs of
        // one feed pass the same values).
        feedFilter: this.filterMode,
        feedSports: this.feedSports ? [...this.feedSports] : null,
        sourceEvidence: this.sourceEvidence,
      });
      // Page lifecycle → the worker's reaper (hidden / frozen ports are not
      // reaped on the 30 s visible threshold).
      try { this.port.postMessage({ type: '__lifecycle', state: documentHidden() ? 'hidden' : 'visible' }); } catch { /* same */ }
      this.installLifecycleListeners();
      // Replay cached orderbook needs if any. Happens on stop()+start()
      // recovery (bfcache resume, stall detector) so the new port gets
      // the same subscriptions the old port had without waiting for
      // components to re-mount.
      if (this.lastDeclaredNeeds.length > 0) {
        try {
          this.port.postMessage({
            type: '__needs', channel: 'orderbooks', tickers: this.lastDeclaredNeeds.slice(),
          });
        } catch { /* port may be closing — extremely unlikely on a just-created port */ }
      }
      if (this.subscribeOpOdds && this.opScopeGames != null) {
        try { this.port.postMessage({ type: '__op_scope', games: this.opScopeGames }); } catch { /* same */ }
      }
      // Policy before needs (#1106): a floor worker learns which ALT keys are
      // a cold standby before this tab's needs can widen it.
      if (this.filterMode.enabled && (this.policyCadenceMs !== (this.filterMode.cadenceMs ?? 250) || this.policyFloorOmit.length > 0)) {
        try { this.port.postMessage({ type: '__filter_policy', cadenceMs: this.policyCadenceMs, floorOmitSports: this.policyFloorOmit }); } catch { /* same */ }
      }
      if ((this.marketsFilterOn || this.opFilterOn) && this.lastDeclaredFilterNeeds != null) {
        try {
          this.port.postMessage({ type: '__filter_needs', needs: serializeNeeds(this.lastDeclaredFilterNeeds) });
        } catch { /* same as above */ }
      }
      this.startWorkerHeartbeat();
      this.startStallDetector();
      this.armGreetTimeout();
      this.installStorageListener();
      // Reset the freshness clock so the stall detector gives the new
      // connection a full threshold window to deliver its first frame
      // instead of inheriting the stale value from before. The greet-replay
      // flag resets too: a teardown between bracket deliveries must not
      // strand it true and suppress live-frame counting on the new port.
      this._lastMessageMs = Date.now();
      this._inGreetReplay = false;
      this.mode = 'shared';
      console.log(`[Hydra:${this.workerName}] using SharedWorker (one upstream WS shared across all tabs) → ${redactUrl(this.url)}`);
      return true;
    } catch (err) {
      console.warn('[Hydra] SharedWorker init failed, falling back to direct WS:', err);
      this.worker = null;
      this.port = null;
      return false;
    }
  }

  /** Tear down and recreate the SharedWorker port at the CURRENT generation.
   *  Port recovery, not instance escape (DNL-R13-P1): "my port may need
   *  reattachment" and "this named worker is irrecoverably sick" are
   *  different states, and conflating them turned every bfcache restore and
   *  quiet-feed stall into a generation bump — a connection-churn generator.
   *  Same-generation reattach is the cheap pre-escape behavior; the ONLY
   *  path that changes generation is the greet-timeout (proven sickness:
   *  a fresh attach that cannot even produce the worker's own `__status`
   *  greet means the worker global is not running). */
  restartShared(reason: string): void {
    if (this.mode !== 'shared' && this.mode !== null) return;
    // The stall restart IS the "first retry the current generation" step:
    // if the replacement attach also stays mute, the greet-timeout escapes.
    this.greetTimeoutRetriedCurrentGen = true;
    console.warn(`[Hydra] tab-side restart: ${reason} → reattaching '${this.workerName}' (gen ${this.workerGen})`);
    this.teardownSharedPort();
    this.startShared();
  }

  /** bfcache-restore reattach: same generation, and the retry ledger is
   *  RESET — a freeze explains any pre-freeze silence, so the restored tab
   *  gets a fresh proof window instead of inheriting a stall streak that
   *  would escape on its first timeout (DNL-R13-P1: a back/forward
   *  restoration must never abandon a healthy worker). */
  reattachShared(reason: string): void {
    if (this.mode !== 'shared' && this.mode !== null) return;
    this.greetTimeoutRetriedCurrentGen = false;
    console.warn(`[Hydra] bfcache reattach: ${reason} → '${this.workerName}' (gen ${this.workerGen})`);
    this.teardownSharedPort();
    this.startShared();
  }

  /** The ONE path that changes generation. Reached only from the
   *  greet-timeout: a fresh attach produced ZERO port messages — not even
   *  the `__status` greet the worker re-broadcasts to every new port — for
   *  GREET_TIMEOUT_MS. Market quiet cannot cause that (the greet is
   *  upstream-independent); only a worker global that is not running can.
   *  Adopt a newer stored generation if another tab already escaped;
   *  otherwise bump. Concurrent bumps compute the same number and converge. */
  private escapeShared(reason: string): void {
    this.escapesThisPage += 1;
    this.lastEscapeMs = Date.now();
    const stored = readWorkerGeneration(this.workerBaseName);
    if (stored > this.workerGen) {
      this.workerGen = stored;
    } else {
      this.workerGen += 1;
      writeWorkerGeneration(this.workerBaseName, this.workerGen);
    }
    this.workerName = decoratedWorkerName(this.workerBaseName, this.workerGen);
    this.greetTimeoutRetriedCurrentGen = false; // new instance earns a fresh retry
    console.warn(`[Hydra] worker escape: ${reason} → spawning '${this.workerName}' (gen ${this.workerGen})`);
    this.teardownSharedPort('__retire');
    this.startShared();
  }

  /** May this client escape now? (See the escape-bounds block above.) */
  private mayEscape(now = Date.now()): boolean {
    return this.escapesThisPage < ESCAPE_MAX_PER_PAGE
      && (this.lastEscapeMs === 0 || now - this.lastEscapeMs >= ESCAPE_MIN_INTERVAL_MS);
  }

  /** Page lifecycle → the worker, and a fresh proof window on return: a tab
   *  that was hidden or frozen explains any silence before it. */
  private installLifecycleListeners(): void {
    if (this.lifecycleListener != null || typeof document === 'undefined' || typeof document.addEventListener !== 'function') return;
    const post = (state: 'visible' | 'hidden' | 'frozen'): void => {
      try { this.port?.postMessage({ type: '__lifecycle', state }); } catch { /* port closing */ }
    };
    const freshWindow = (): void => {
      this._lastMessageMs = Date.now();
      this.greetTimeoutRetriedCurrentGen = false;
      if (this.greetTimeoutTimer) this.armGreetTimeout();
    };
    this.lifecycleListener = () => {
      const hidden = documentHidden();
      post(hidden ? 'hidden' : 'visible');
      if (!hidden) freshWindow();
    };
    this.freezeListener = () => post('frozen');
    this.resumeListener = () => { post(documentHidden() ? 'hidden' : 'visible'); freshWindow(); };
    document.addEventListener('visibilitychange', this.lifecycleListener);
    document.addEventListener('freeze', this.freezeListener);
    document.addEventListener('resume', this.resumeListener);
  }

  private removeLifecycleListeners(): void {
    if (typeof document === 'undefined') return;
    if (this.lifecycleListener) document.removeEventListener('visibilitychange', this.lifecycleListener);
    if (this.freezeListener) document.removeEventListener('freeze', this.freezeListener);
    if (this.resumeListener) document.removeEventListener('resume', this.resumeListener);
    this.lifecycleListener = this.freezeListener = this.resumeListener = null;
  }

  /** A peer tab escaped to a newer generation (its storage write fired our
   *  `storage` listener). Migrate even while healthy: without this, tabs
   *  whose port to the OLD instance is fine never observe the write and
   *  keep the abandoned instance's upstream socket alive indefinitely —
   *  the unbounded double-socket Danielle flagged (DNL-R13-P1). Adopt-only:
   *  never bumps, never writes. */
  private migrateToGeneration(gen: number, source: string, immediate = false): void {
    if (this.mode !== 'shared' || this.stopped || gen <= this.workerGen) return;
    const now = Date.now();
    if (!immediate && this.lastMigrationMs > 0 && now - this.lastMigrationMs < PEER_MIGRATION_MIN_INTERVAL_MS) {
      // Coalesce: follow the newest generation once the interval is up.
      this.pendingMigrationGen = Math.max(this.pendingMigrationGen, gen);
      if (this.deferredMigration == null) {
        this.deferredMigration = setTimeout(() => {
          this.deferredMigration = null;
          const target = Math.max(this.pendingMigrationGen, readWorkerGeneration(this.workerBaseName));
          this.pendingMigrationGen = 0;
          this.migrateToGeneration(target, `${source} (deferred)`, true);
        }, PEER_MIGRATION_MIN_INTERVAL_MS - (now - this.lastMigrationMs));
      }
      return;
    }
    if (this.deferredMigration) { clearTimeout(this.deferredMigration); this.deferredMigration = null; }
    this.pendingMigrationGen = 0;
    this.lastMigrationMs = now;
    this.workerGen = gen;
    this.workerName = decoratedWorkerName(this.workerBaseName, this.workerGen);
    this.greetTimeoutRetriedCurrentGen = false;
    console.warn(`[Hydra] generation migration (${source}) → '${this.workerName}' (gen ${this.workerGen})`);
    // Close the old socket BEFORE opening the replacement: `__retire` makes a
    // healthy old instance close its upstream at once; the new attach waits
    // a short settle for it.
    this.teardownSharedPort('__retire');
    if (this.retireSettleTimer) clearTimeout(this.retireSettleTimer);
    this.retireSettleTimer = setTimeout(() => {
      this.retireSettleTimer = null;
      if (!this.stopped && this.port == null) this.startShared();
    }, RETIRE_SETTLE_MS);
  }

  /** Sickness detector: fires only when a fresh attach produced zero port
   *  messages for GREET_TIMEOUT_MS. Retry the current generation once (a
   *  transiently-hosed port deserves a second chance), then escape. The
   *  alternation also caps pathological churn: an environment where NOTHING
   *  ever attaches mints a generation at most every 2×GREET_TIMEOUT_MS,
   *  versus the pre-escape behavior of a doomed reattach every ~7 s. */
  private armGreetTimeout(): void {
    this.clearGreetTimeout();
    const timeoutMs = this.greetTimeoutMs > 0 ? this.greetTimeoutMs : this.GREET_TIMEOUT_MS;
    this.greetTimeoutTimer = setTimeout(() => {
      this.greetTimeoutTimer = null;
      if (this.stopped || this.mode !== 'shared') return;
      // Silence in a hidden tab, or in a tab behind on its own messages, says
      // nothing about the worker: wait (the visible/resume handler re-arms).
      if (documentHidden() || tabIsLagging()) { this.armGreetTimeout(); return; }
      if (this.greetTimeoutRetriedCurrentGen) {
        if (this.mayEscape()) {
          this.greetTimeoutMs = 0;
          this.escapeShared(`no port message within ${timeoutMs}ms of a retried attach`);
          return;
        }
        // Escape budget spent: retry the SAME generation, backing off.
        this.greetTimeoutMs = Math.min(GREET_TIMEOUT_MAX_MS, timeoutMs * 2);
        console.warn(`[Hydra] mute attach on '${this.workerName}' (gen ${this.workerGen}); escape budget spent — retrying in ${this.greetTimeoutMs}ms`);
        this.teardownSharedPort();
        this.startShared();
      } else {
        this.greetTimeoutRetriedCurrentGen = true;
        console.warn(`[Hydra] mute attach: retrying '${this.workerName}' (gen ${this.workerGen}) once before escaping`);
        this.teardownSharedPort();
        this.startShared();
      }
    }, timeoutMs);
  }

  private clearGreetTimeout(): void {
    if (this.greetTimeoutTimer) {
      clearTimeout(this.greetTimeoutTimer);
      this.greetTimeoutTimer = null;
    }
  }

  /** Idempotent install of the peer-escape observer. `storage` fires only in
   *  OTHER tabs, so a healthy tab learns a peer escaped within milliseconds
   *  of the write and migrates — the convergence bound DNL-R13-P1 asked for. */
  private installStorageListener(): void {
    if (this.storageListener != null) return;
    if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return;
    this.storageListener = (ev: StorageEvent) => {
      if (ev.key !== WORKER_GEN_STORAGE_PREFIX + this.workerBaseName) return;
      const gen = ev.newValue == null ? NaN : Number.parseInt(ev.newValue, 10);
      if (!Number.isFinite(gen)) return;
      this.migrateToGeneration(gen, 'peer tab escaped');
    };
    window.addEventListener('storage', this.storageListener);
  }

  private removeStorageListener(): void {
    if (this.storageListener != null && typeof window !== 'undefined') {
      window.removeEventListener('storage', this.storageListener);
      this.storageListener = null;
    }
  }

  /** Retire this port before replacing it. Worker caches are keyed by port;
   *  startShared replays this client's needs onto the replacement port.
   *  Reaper retention is only for suspended ports that can still resume. */
  private teardownSharedPort(goodbye: '__bye' | '__retire' = '__bye'): void {
    this.stopWorkerHeartbeat();
    this.stopStallDetector();
    this.clearGreetTimeout();
    if (this.port) {
      try { this.port.postMessage({ type: goodbye }); } catch { /* already unavailable */ }
      try { this.port.close(); } catch { /* ignore */ }
      this.port = null;
    }
    this.worker = null;
    this.workerOpInstalled = false; // the replacement port's greet replays it
    this.setConnectedState(false);
    this.setBackpressureLevel(0);
    this.callbacks.onStatus(false);
  }

  // ── Direct-WS path ─────────────────────────────────────────────

  /** The direct socket's oddspapi link (created on first use; its legacy
   *  mark must outlive the socket it was set on — D1a step 6). */
  private ensureDirectOpLink(): ChannelFilterLink<OpFilter> {
    if (this.directOpLink) return this.directOpLink;
    this.directOpLink = new ChannelFilterLink<OpFilter>({
      channel: 'oddspapi',
      send: (msg) => this.send(msg),
      // D1a: leave the socket. forceReconnect detaches it (`this.ws` moves
      // on, and every handler speaks only for `this.ws`), closes it and
      // opens the next one, which runs oddspapi bare for its whole life.
      reconnect: (reason) => {
        console.warn(`[Hydra] oddspapi filter ${reason} on ${this.url} — reconnecting; the next socket subscribes oddspapi unfiltered`);
        this.forceReconnect();
      },
      legacyOps: () => [{ op: 'subscribe', channel: 'oddspapi' }, { op: 'snapshot', channel: 'oddspapi' }],
    });
    return this.directOpLink;
  }

  /** The OP filter for the tab's union on the wire — the EMITTED needs, so
   *  a held route transaction never leaks early (as on markets). Folds a
   *  declaration made before this socket existed, synchronously. */
  private directOpFilterNow(): OpFilter {
    const agg = this.ensureDirectFilterAggregator();
    this.directSubscribing = true;
    try {
      if (this.lastDeclaredFilterNeeds != null && !agg.hasSource('tab')) agg.setNeeds('tab', this.lastDeclaredFilterNeeds);
    } finally {
      this.directSubscribing = false;
    }
    return buildOpFilter(agg.emitted().odds);
  }

  private subscribeDirectOpNow(): void {
    if (this.deferredOpSubscribe) { clearTimeout(this.deferredOpSubscribe); this.deferredOpSubscribe = null; }
    if (!this.directOpLink || !this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.directOpLink.subscribe(this.directOpFilterNow());
  }

  private connect(): void {
    if (this.stopped) return;
    this.mode = 'direct';

    console.log(`[Hydra] connecting to ${redactUrl(this.url)}...`);

    try {
      this.ws = new WebSocket(this.url);
      // Socket identity guard (Danielle R1 DNL-001): forceReconnect() closes
      // one socket and opens the next immediately, and the browser may
      // deliver the OLD socket's close (or a straggling message) AFTER the
      // replacement has opened and installed its fence. Every handler below
      // therefore speaks only while its socket is still `this.ws`; a
      // superseded socket's callbacks mutate nothing — no fence reset, no
      // status flip, no extra reconnect.
      const sock = this.ws;
      const owned = (): boolean => this.ws === sock;

      this.ws.onopen = () => {
        if (!owned()) return;
        this.sourceAgeGeneration = crypto.randomUUID();
        this.noteUpstream(true);
        this.setConnectedState(true);
        this.reconnectAttempts = 0;
        console.log('[Hydra] connected');
        this.callbacks.onStatus(true);
        // Socket boundary: nothing from this socket is admissible until ITS
        // ack installs a pair (A2′). The filter itself is carried across by
        // subscribeMarketsChannel below — client-side failover carry. A
        // degraded socket's fallback does not carry over: every socket
        // retries the filter.
        this.directFence.reset();
        this.directLegacyFallback = false;
        this.clearDirectAckTimer();
        // The announce is per connection; the claim scope is the ALT-only
        // hint until this socket's own announce lands.
        this.directAnnouncedSports = null;
        this.directAnnouncedAxes = null;

        // Dedicated OP transport: only the oddspapi channel — never
        // markets/unabated (the fleet's other connections own those).
        if (!this.opOnly && !this.booksOnly) {
          // Markets subscription, scoped to the current sport filter (or broad
          // when none declared / '*'). Orderbooks channel is filtered to
          // declared needs — replay on reconnect from current declared set.
          // Always snapshot: hydra's `markets` channel doesn't auto-snapshot on
          // subscribe (only `orderbooks` does), and skipping the snapshot leaves
          // slow-trading tickers blank until their next live frame.
          if (this.filterMode.enabled && this.lastDeclaredFilterNeeds == null) {
            if (this.deferredMarketsSubscribe) clearTimeout(this.deferredMarketsSubscribe);
            this.deferredMarketsSubscribe = setTimeout(() => {
              this.deferredMarketsSubscribe = null;
              if (owned()) this.subscribeMarketsChannel();
            }, HydraClient.FIRST_NEEDS_WAIT_MS);
          } else {
            this.subscribeMarketsChannel();
          }

          // #1191: Core retired Unabated. Do not subscribe to its dead channel.
        }

        // OddsPapi comparison odds — OP-capable feeds only (DNL-003,
        // failover rev: the env-preferred source plus the OG standby). The
        // channel is EXCLUDED from the legacy `all` firehose (contract rule
        // 11: `all` has no snapshot, so its deltas would be baseline-less),
        // so the explicit subscribe + snapshot pair is the ONLY way to
        // receive state. Bare subscribe is silent, like `unabated`.
        if (this.subscribeOpOdds && !this.opFilterOn) {
          this.send({ op: 'subscribe', channel: 'oddspapi' });
          this.send({ op: 'snapshot',  channel: 'oddspapi' });
        } else if (this.subscribeOpOdds) {
          // The odds filter (odds-gamestate-feed-filters): this socket's
          // link decides — the filter-bearing subscribe (the install streams
          // the scoped snapshot itself), or, for one socket after a D1a
          // expiry, the bare pair above. A socket opened before any view
          // declared waits briefly for the declaration, like markets.
          const mode = this.ensureDirectOpLink().openSocket();
          this.workerOpInstalled = false;
          if (mode === 'filtered' && this.lastDeclaredFilterNeeds == null) {
            if (this.deferredOpSubscribe) clearTimeout(this.deferredOpSubscribe);
            this.deferredOpSubscribe = setTimeout(() => {
              this.deferredOpSubscribe = null;
              if (owned()) this.subscribeDirectOpNow();
            }, HydraClient.FIRST_NEEDS_WAIT_MS);
          } else {
            this.subscribeDirectOpNow();
          }
        }

        // Clear upstream state (connection just dropped) and let reconcile
        // rebuild the orderbook subscription from the current needs set.
        this.directUpstreamOrderbookTickers.clear();
        for (const t of this.directPendingUnsubscribeTimers.values()) clearTimeout(t);
        this.directPendingUnsubscribeTimers.clear();
        this.reconcileDirectOrderbookSubscriptions();

        this.startPingLoop();
      };

      this.ws.binaryType = 'arraybuffer';
      this.ws.onmessage = (event) => {
        if (!owned()) return;
        try {
          const raw = event.data instanceof ArrayBuffer
            ? new TextDecoder().decode(event.data)
            : event.data as string;
          const msg = JSON.parse(raw) as HydraServerMsg;
          this._lastMessageMs = Date.now();
          this._directTally.frames += 1;
          this._directTally.bytes += raw.length;
          const kind = typeof msg.type === 'string' ? msg.type : 'unknown';
          this._directTally.byType[kind] = (this._directTally.byType[kind] ?? 0) + 1;
          this.handleMessage(msg, captureSourceAge(msg, this.sourceAgeGeneration));
        } catch (err) {
          console.warn('[Hydra] parse error:', err);
        }
      };

      this.ws.onclose = (event) => {
        if (!owned()) return; // a superseded socket's delayed close
        this.noteUpstream(false);
        this.setConnectedState(false);
        this.stopPingLoop();
        this.clearDirectAckTimer();
        this.closeDirectOpLink();
        if (this.deferredMarketsSubscribe) { clearTimeout(this.deferredMarketsSubscribe); this.deferredMarketsSubscribe = null; }
        this.directFence.reset();
        this.cancelDirectFloorInstall(); // the next socket's subscribe carries the composed filter
        // An unexpected close while at level >= 2 is almost certainly a
        // backpressure shed — level 3 is an abrupt terminate and the 4002
        // close frame may never be delivered on a saturated socket.
        if (this._backpressureLevel >= 2) {
          console.warn(`[Hydra] close after backpressure level ${this._backpressureLevel} — treating as backpressure shed`);
        }
        this.setBackpressureLevel(0);
        console.log(`[Hydra] disconnected (code=${event.code}, reason=${event.reason || 'none'}, clean=${event.wasClean})`);
        this.callbacks.onStatus(false);
        this.scheduleReconnect();
      };

      this.ws.onerror = () => {
        if (!owned()) return;
        console.warn(`[Hydra] ws error — connection to ${this.url} failed. Check: 1) Is Hydra running? 2) Does the URL support WebSocket upgrade? 3) Is port accessible?`);
      };
    } catch (err) {
      console.error('[Hydra] connect failed:', err);
      this.scheduleReconnect();
    }
  }

  private send(msg: Record<string, unknown>, reason: SnapshotReason = 'unspecified'): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify(withSourceEvidence(msg, this.sourceEvidence)));
    if ((msg.op === 'snapshot' || msg.op === 'subscribe') && (msg.channel === 'markets' || msg.channel === 'oddspapi')) {
      const pair = msg.channel === 'markets' ? this.directFence.pair : this.directOpLink?.fence.pair;
      this.snapshotTraceBuffer.add({ atMs: Date.now(), operation: msg.op, channel: msg.channel,
        reason: msg.op === 'subscribe' ? 'subscription' : reason, requester: 'direct-tab',
        socketIncarnation: pair?.socketIncarnation ?? null, filterGeneration: pair?.filterGeneration ?? null });
    }
  }

  /** Direct-WS path: reconcile orderbook subscription to match needs.
   *  Adds subscribe immediately; removes are debounced 30 s so rapid
   *  view changes that touch the same tickers don't churn. Mirror of
   *  the SharedWorker-side logic in hydraWorker.ts. */
  private reconcileDirectOrderbookSubscriptions(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    const desired = this.directOrderbookNeeds;

    const toAdd: string[] = [];
    for (const ticker of desired) {
      if (!this.directUpstreamOrderbookTickers.has(ticker)) toAdd.push(ticker);
      const pending = this.directPendingUnsubscribeTimers.get(ticker);
      if (pending) {
        clearTimeout(pending);
        this.directPendingUnsubscribeTimers.delete(ticker);
      }
    }

    for (const ticker of this.directUpstreamOrderbookTickers) {
      if (desired.has(ticker)) continue;
      if (this.directPendingUnsubscribeTimers.has(ticker)) continue;
      const timer = setTimeout(() => {
        this.directPendingUnsubscribeTimers.delete(ticker);
        if (this.directOrderbookNeeds.has(ticker)) return;
        this.directUpstreamOrderbookTickers.delete(ticker);
        this.send({ op: 'unsubscribe', channel: 'orderbooks', tickers: [ticker] });
      }, this.UNSUBSCRIBE_GRACE_MS);
      this.directPendingUnsubscribeTimers.set(ticker, timer);
    }

    if (toAdd.length > 0) {
      for (const t of toAdd) this.directUpstreamOrderbookTickers.add(t);
      this.send({ op: 'subscribe', channel: 'orderbooks', tickers: toAdd });
    }
  }

  /** Direct-WS onopen. Flag off: the legacy subscribe + snapshot
   *  (connectMarketOps — sport-unfiltered, props behind the sticky opt-in;
   *  see sportFilter.ts). Flag on: the subscribe carries the complete current filter (the aggregator
   *  seeds the defaults before the first connect), and the ack's replacement
   *  snapshot replaces the explicit snapshot op. */
  private subscribeMarketsChannel(): void {
    let filter: MarketFilter | null = null;
    if (this.filterMode.enabled) {
      const agg = this.ensureDirectFilterAggregator();
      // Needs declared before this socket existed (or on a previous one)
      // are folded in SYNCHRONOUSLY so the first subscribe already carries
      // the complete filter — never a default connect followed by a
      // widening replace + second snapshot.
      this.directSubscribing = true;
      try {
        if (this.lastDeclaredFilterNeeds != null && !agg.hasSource('tab')) agg.setNeeds('tab', this.lastDeclaredFilterNeeds);
        agg.flushPending();
      } finally {
        this.directSubscribing = false;
      }
      filter = this.directFilter;
    }
    for (const op of connectMarketOps(filter, this.directPropsWanted, this.marketsSports)) this.send(op);
    if (filter) {
      // The connect subscribe IS a send of this filter: seed the limiter's
      // key (never re-send it) but not its window — the first change after
      // a connect still goes out at once.
      this.directLastReplaceKey = filterKey(filter); this.directLastReplaceMs = 0;
      if (this.directPendingReplace) { clearTimeout(this.directPendingReplace); this.directPendingReplace = null; }
      this.clearDirectAckTimer();
      const sock = this.ws;
      this.directAckTimer = setTimeout(() => {
        this.directAckTimer = null;
        if (this.ws === sock) this.degradeDirectToLegacy(`no filterAck within ${HydraClient.ACK_TIMEOUT_MS}ms`);
      }, HydraClient.ACK_TIMEOUT_MS);
    }
  }

  // ── Message handling ───────────────────────────────────────────

  private sourceAgeGeneration = crypto.randomUUID();
  private sourceContinuity = new SourceContinuityTransport();

  private handleMessage(msg: HydraServerMsg, sourceAge?: SourceAge, replay = false): void {
    // Any parsed server frame — including pong — proves the transport is
    // alive. Data-freshness bookkeeping stays with the per-type handlers.
    this.callbacks.onKeepalive?.();
    if (!replay) this.callbacks.onTransportAlive?.(0);
    switch (msg.type) {
      case '__sourceContinuityInvalid': {
        const lane = this.sourceContinuity.quarantine(msg.lane);
        if (lane) this.callbacks.onSourceContinuity?.(null, lane);
        else this.callbacks.onSourceContinuity?.(null);
        break;
      }
      case 'sourceContinuity': {
        if (!this._connected) break;
        const result = this.sourceContinuity.receive(msg);
        if (result === 'accepted') this.callbacks.onSourceContinuity?.(parseSourceContinuity(msg));
        else if (result === 'invalid') this.callbacks.onSourceContinuity?.(null);
        else if (result === 'quarantined') this.callbacks.onSourceContinuity?.(null, msg.lane);
        break;
      }
      case 'market':
        // Direct-WS fencing (A2′): on a filtered socket only frames stamped
        // with the installed pair are admissible; everything else — an older
        // generation, a previous socket, an unstamped legacy frame — is
        // dropped before it can touch the store. Shared mode arrives here
        // already fenced by the worker.
        if (this.mode === 'direct' && this.filterMode.enabled && !this.opOnly && !this.directLegacyFallback && !this.directFence.admits(msg)) {
          this._marketFramesDropped++;
          break;
        }
        this._marketsReceived++;
        if (!replay) this._liveMarketFrames++;
        // Main's per-venue tally keys US off the `PMUS-` ticker, so the lane's
        // `polymarket_us` source folds to the Poly venue it counts under.
        if (!replay) countReceivedMarket(msg.market.ticker, msg.market.source === 'polymarket_us' ? 'polymarket' : msg.market.source);
        this.callbacks.onMarket(msg.market, sourceAge);
        break;
      case 'marketRemoved': {
        // Same fence as a market frame, for the same reason: on a filtered
        // socket only the installed pair is admissible. A removal from an
        // older generation or a superseded socket describes a scope we no
        // longer hold and must never evict live state. In shared mode the
        // worker fenced it before forwarding.
        if (this.mode === 'direct' && this.filterMode.enabled && !this.opOnly && !this.directLegacyFallback
          && !this.directFence.admits(msg)) {
          this._marketFramesDropped++;
          break;
        }
        if (typeof msg.ticker !== 'string' || !msg.ticker) break;
        this._marketsRemoved++;
        if (msg.socketIncarnation == null && msg.filterGeneration == null) {
          this.callbacks.onMarketRemoved?.(msg.ticker);
        } else if (typeof msg.socketIncarnation === 'string' && typeof msg.filterGeneration === 'number'
          && Number.isSafeInteger(msg.filterGeneration)) {
          this.callbacks.onMarketRemoved?.(msg.ticker, {
            socketIncarnation: msg.socketIncarnation, filterGeneration: msg.filterGeneration,
          });
        }
        break;
      }
      case 'filterAck':
        if ((msg as { channel?: unknown }).channel === 'oddspapi' || (msg as { channel?: unknown }).channel === 'gamestate') {
          // A channel filter's ack (odds-gamestate-feed-filters): the OP
          // link's own fence, never the markets one, and never the markets
          // health surfaces. In shared mode the worker consumed it.
          if (this.mode === 'direct' && (msg as { channel?: unknown }).channel === 'oddspapi' && this.directOpLink) {
            const res = this.directOpLink.onAck(msg as unknown as HydraChannelFilterAckMsg);
            if (res?.kind === 'installed') this.noteOpAlive();
          }
          break;
        }
        if ((msg as { channel?: unknown }).channel !== 'markets') {
          // Untagged (or a channel we do not speak): dropped and counted,
          // never applied to any fence and never the markets health surfaces
          // (directory-edgelike-cores D5). Every engine that speaks a filter
          // tags its acks; an untagged one is a source that does not enforce
          // the filter answering generically. A channel filter still waiting
          // for ITS ack keeps waiting; D1a's 8 s rule applies unchanged.
          this._untaggedFilterAcks++;
          break;
        }
        if (this.mode === 'direct') {
          this.clearDirectAckTimer(); // the engine speaks the filter
          const res = this.directFence.onAck(msg);
          if (res.kind === 'installed') {
            this.directLegacyFallback = false; // a (late) ack re-arms the fence
            // Reset BEFORE any frame of this generation can be admitted —
            // the ack precedes them on the wire, and this handler runs
            // synchronously in wire order.
            this.callbacks.onFilterReset?.({ ...res.pair, scope: res.effective });
          } else if (res.kind === 'rejected') {
            console.warn(`[Hydra] markets filter rejected (${res.error}) — prior filter/generation retained`);
            // A rejected FIRST filter activates nothing server-side: degrade
            // rather than sit dark (the object is ours; the log shows why).
            this.degradeDirectToLegacy(`filter rejected: ${res.error}`);
          }
        }
        this.callbacks.onFilterAck?.(msg);
        break;
      case 'orderbookUnavailable':
        if (typeof msg.ticker === 'string' && msg.ticker) this.callbacks.onOrderbookUnavailable?.(msg.ticker);
        break;
      case 'orderbook':
        this._orderbooksReceived++;
        if (!replay) countReceivedBook(msg.ticker);
        // Orderbook frames resuming IS the level-2 recovery signal — hydra
        // suspends them entirely at level >= 2 and sends no "recovered"
        // message. Every frame is a full book, so state self-heals per
        // ticker; for quiet tickers, re-issuing the filtered subscribe
        // triggers hydra's per-ticker auto-snapshot (direct mode only —
        // in shared mode hydraWorker owns the subscription and does this).
        if (this._backpressureLevel >= 2) {
          this.setBackpressureLevel(0);
          if (this.mode === 'direct' && this.directUpstreamOrderbookTickers.size > 0) {
            this.send({
              op: 'subscribe',
              channel: 'orderbooks',
              tickers: [...this.directUpstreamOrderbookTickers],
            });
          }
        }
        this.callbacks.onOrderbook(msg.ticker, msg.orderbook, sourceAge, {
          lane: msg.lane, sourceEpoch: msg.sourceEpoch, sourceGen: msg.sourceGen,
          observedAtMs: msg.observedAtMs, snapshotMs: msg.snapshotMs, pendingResync: msg.pendingResync,
        });
        break;
      case 'consensus':
        if (!replay) countReceivedConsensus();
        this.callbacks.onConsensus(msg.consensus, msg.serverTs, sourceAge);
        break;
      case 'opOdds':
      case 'opSnapshotBegin':
      case 'opSnapshotEnd':
        // DNL-003: only an OP-capable client (the preferred source or the
        // OG standby) may dispatch; an oddspapi frame on any other feed is
        // dropped with a counted diagnostic. The active-source gate in
        // useHydra then keeps exactly one ingestion authority at any
        // instant.
        if (!this.subscribeOpOdds) {
          this._opFramesDropped++;
          if (this._opFramesDropped === 1) {
            console.warn(`[Hydra:${this.workerName}] dropping oddspapi frames — this feed is not the OP ingestion authority (DNL-003)`);
          }
          break;
        }
        // The odds filter (direct mode): the installed pair only on a
        // filtered socket, unstamped only on a legacy one (D1a). Shared
        // mode arrives here already fenced by the worker.
        if (this.mode === 'direct' && this.directOpLink && !this.directOpLink.admits(msg)) {
          this._opFramesFenced++;
          break;
        }
        // Stamped for EVERY capable feed, active or standby, before the
        // active-source gate upstream — the same "record delivery, then
        // gate" order onMarket uses. That is what makes it usable as
        // proof-of-service for a standby we are considering promoting.
        this._lastOpFrameMs = Date.now();
        if (msg.type === 'opOdds') {
          if (!replay) countReceivedOpOdds();
          this.callbacks.onOpOdds?.(msg);
        } else if (msg.type === 'opSnapshotBegin') this.callbacks.onOpSnapshotBegin?.(msg);
        else this.callbacks.onOpSnapshotEnd?.(msg);
        break;
      case 'gameState':
      case 'gsSnapshotBegin':
      case 'gsSnapshotEnd':
        // Unlike the oddspapi lane there is no single-authority gate here: a
        // game-state frame is absolute and rev-ordered per key, so two feeds
        // delivering the same key converge rather than fight. The store's rev
        // guard is what makes that safe, and it is also what makes a late
        // frame from a slower socket a no-op instead of a revert.
        if (msg.type === 'gameState') this.callbacks.onGameState?.(msg);
        else if (msg.type === 'gsSnapshotBegin') this.callbacks.onGsSnapshotBegin?.(msg);
        else this.callbacks.onGsSnapshotEnd?.(msg);
        break;
      case 'activeSports': {
        // Complete replacement set — replace, never merge. Duplicates
        // re-arrive on every subscribe op (worker greet replays too).
        if (this.mode === 'direct') {
          this.directAnnouncedSports = Array.isArray(msg.sports) ? msg.sports.filter((x): x is string => typeof x === 'string') : [];
          this.directAnnouncedAxes = readFilterAxes(msg);
          this.refreshDirectFilterOnAnnounce();
        }
        const sports = Array.isArray(msg.sports) ? msg.sports : [];
        this._engineIdle = sports.length === 0;
        const axes = readFilterAxes(msg) ? [...readFilterAxes(msg)!] : undefined;
        // Keep legacy callback arity; present [] must remain an explicit withdrawal.
        if (msg.activeFeeds === undefined) this.callbacks.onActiveSports?.(sports, axes);
        else this.callbacks.onActiveSports?.(sports, axes, readFeedPairs(msg.activeFeeds));
        break;
      }
      case 'pong':
        // Keepalive acknowledged. On a filtered oddspapi socket with an
        // installed generation, it is also the channel's liveness (see
        // noteOpAlive).
        if (this.mode === 'direct' && this.directOpLink?.installed) this.noteOpAlive();
        break;
      case 'backpressure':
        console.warn(`[Hydra] backpressure level=${msg.level} pending=${msg.pending}: ${msg.message}`);
        // Track it so orderbook-driven views can show a stale indicator
        // while hydra has our orderbook frames suspended (level >= 2).
        // No proactive reconnect: market + consensus still flow at level
        // 2, and hydra's server-side level2EscalateAfterMs force-
        // disconnects hopeless consumers (abrupt terminate — the 4002
        // close frame may never arrive), which our existing
        // reconnect-with-backoff handles.
        // The worker's first-heard time rides only its greet replay; a live
        // frame (or a raw hydra frame in direct mode) stamps receipt time.
        this.setBackpressureLevel(typeof msg.level === 'number' ? msg.level : 0,
          replay ? (msg as { __bpSinceMs?: unknown }).__bpSinceMs : undefined);
        break;
      case 'error':
        console.error(`[Hydra] error: ${msg.message}`);
        break;
    }
  }

  // ── SharedWorker heartbeat ─────────────────────────────────────

  /** Liveness ping to the SharedWorker so its reaper knows this tab is
   *  alive. Without this, a tab that dies without firing pagehide (crash,
   *  OS kill, mobile background evict) leaves its declared orderbook needs
   *  in the worker's union forever, monotonically growing the upstream
   *  subscription. See `hydraWorker.ts` reaper for the matching half. */
  private startWorkerHeartbeat(): void {
    this.stopWorkerHeartbeat();
    this.workerHeartbeatTimer = setInterval(() => {
      if (!this.port) return;
      try { this.port.postMessage({ type: '__heartbeat' }); } catch { /* port closing */ }
    }, this.WORKER_HEARTBEAT_INTERVAL_MS);
  }

  private stopWorkerHeartbeat(): void {
    if (this.workerHeartbeatTimer) {
      clearInterval(this.workerHeartbeatTimer);
      this.workerHeartbeatTimer = null;
    }
  }

  /** Tab-side stall detector. Fires every TAB_STALL_CHECK_INTERVAL_MS;
   *  if we still think we're connected but the port has gone silent for
   *  TAB_STALL_THRESHOLD_MS, the worker has stopped feeding us (port
   *  reaped during a freeze, worker died, etc) and we restart the
   *  SharedWorker connection. The worker's own watchdogs cover the
   *  upstream-silence side; this only catches gaps in the local
   *  port→tab leg that the worker can't observe. Counterpart to the
   *  worker's `reaper.dropped` / `port.resurrect` events. */
  private startStallDetector(): void {
    this.stopStallDetector();
    this.stallDetectorTimer = setInterval(() => {
      if (this.stopped) return;
      if (!this._connected) return;
      if (this._lastMessageMs === 0) return;
      // An idle engine (empty activeSports announce) has nothing to send;
      // its silence is by design. The worker's own upstream watchdog still
      // covers a dead socket there.
      if (this._engineIdle) return;
      // A hidden tab's silence is throttling, not a stalled port.
      if (documentHidden()) return;
      const ageMs = Date.now() - this._lastMessageMs;
      if (ageMs > this.tabStallThresholdMs) {
        this.restartShared(`port silent for ${ageMs}ms`);
      }
    }, this.TAB_STALL_CHECK_INTERVAL_MS);
  }

  private stopStallDetector(): void {
    if (this.stallDetectorTimer) {
      clearInterval(this.stallDetectorTimer);
      this.stallDetectorTimer = null;
    }
  }

  // ── Ping / Reconnect (direct-WS path only) ────────────────────

  private startPingLoop(): void {
    this.stopPingLoop();
    this.pingTimer = setInterval(() => {
      this.send({ op: 'ping', ts: Date.now() });
    }, PING_INTERVAL);
  }

  private stopPingLoop(): void {
    if (this.pingTimer) { clearInterval(this.pingTimer); this.pingTimer = null; }
  }

  private scheduleReconnect(): void {
    if (this.stopped) return;
    const delay = Math.min(BASE_RECONNECT_DELAY * 2 ** this.reconnectAttempts, MAX_RECONNECT_DELAY);
    this.reconnectAttempts++;
    console.log(`[Hydra] reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);
    this.reconnectTimer = setTimeout(() => this.connect(), delay);
  }

  /** Socket boundary for the direct oddspapi link. */
  private closeDirectOpLink(): void {
    if (this.deferredOpSubscribe) { clearTimeout(this.deferredOpSubscribe); this.deferredOpSubscribe = null; }
    this.directOpLink?.closeSocket();
  }

  private clearTimers(): void {
    this.stopPingLoop();
    this.clearDirectAckTimer();
    this.closeDirectOpLink();
    if (this.deferredMarketsSubscribe) { clearTimeout(this.deferredMarketsSubscribe); this.deferredMarketsSubscribe = null; }
    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
    for (const t of this.directPendingUnsubscribeTimers.values()) clearTimeout(t);
    this.directPendingUnsubscribeTimers.clear();
  }
}

/** Structured-clone-safe copy of a needs declaration for postMessage (Sets
 *  and iterables become arrays; `'all'` survives verbatim). */
/** The tab -> SharedWorker boundary. `FeedNeeds` accepts any Iterable, which
 *  is not structured-cloneable, so each axis is materialised here.
 *
 *  EVERY axis must be listed. This is the second of two publisher boundaries a
 *  declaration crosses (`toFeedNeeds` is the first), and in shared mode — which
 *  is production — it is the one that decides what the worker ever sees. The
 *  games axis was dropped here after being fixed in the other one, so the page
 *  declared its event, the union carried it, and the wire still went out
 *  sport-only with no error anywhere. Adding an axis means adding it in BOTH
 *  places; the tests at each boundary assert the shape covers them all. */
function serializeNeeds(needs: FeedNeeds | null): { tickers?: string[]; classes?: string[]; marketTypes?: string[]; sports?: string[] | 'all'; namedSports?: string[]; games?: string[] | 'all'; lineScope?: 'main' | 'all' | 'any'; cadenceMs?: number | null; floorSports?: string[]; provisional?: boolean; classesBySport?: Record<string, string[]>; marketTypesBySport?: Record<string, string[]>; tickersBroadFor?: string[]; gamesBroadFor?: string[]; namedTickers?: string[]; namedGames?: string[]; altGames?: string[]; odds?: { board?: { sports: string[] | 'all'; marketTypes: string[]; periods: string[] }; detailGames?: string[] } } | null {
  if (needs == null) return null;
  return {
    tickers: needs.tickers ? [...needs.tickers] : undefined,
    classes: needs.classes ? [...needs.classes] : undefined,
    marketTypes: needs.marketTypes ? [...needs.marketTypes] : undefined,
    sports: needs.sports === undefined ? undefined : needs.sports === 'all' ? 'all' : [...needs.sports],
    // Names beside a broad union — the ALT-only mute's carrier (D5.4).
    namedSports: needs.namedSports ? [...needs.namedSports] : undefined,
    games: needs.games === undefined ? undefined : needs.games === 'all' ? 'all' : [...needs.games],
    // Line scope crosses the port verbatim (absent stays absent = broad).
    lineScope: needs.lineScope,
    cadenceMs: needs.cadenceMs,
    floorSports: needs.floorSports ? [...needs.floorSports] : undefined,
    provisional: needs.provisional === true ? true : undefined,
    classesBySport: needs.classesBySport ? Object.fromEntries(Object.entries(needs.classesBySport).map(([k, v]) => [k, [...v]])) : undefined,
    marketTypesBySport: needs.marketTypesBySport ? Object.fromEntries(Object.entries(needs.marketTypesBySport).map(([k, v]) => [k, [...v]])) : undefined,
    tickersBroadFor: needs.tickersBroadFor ? [...needs.tickersBroadFor] : undefined,
    gamesBroadFor: needs.gamesBroadFor ? [...needs.gamesBroadFor] : undefined,
    namedTickers: needs.namedTickers ? [...needs.namedTickers] : undefined,
    namedGames: needs.namedGames ? [...needs.namedGames] : undefined,
    // ALT events cross the port (wedge issue 1) — see FeedNeeds.altGames.
    altGames: needs.altGames ? [...needs.altGames] : undefined,
    // OddsPapi facets (odds-gamestate-feed-filters D6) — the worker builds
    // every OP-capable socket's oddspapi filter from these.
    odds: needs.odds ? {
      board: needs.odds.board ? {
        sports: needs.odds.board.sports === 'all' ? 'all' : [...needs.odds.board.sports],
        marketTypes: [...needs.odds.board.marketTypes],
        periods: [...needs.odds.board.periods],
      } : undefined,
      detailGames: needs.odds.detailGames ? [...needs.odds.detailGames] : undefined,
    } : undefined,
  };
}
